/* eslint-disable default-case */
import _ from "lodash/fp";
import { v4 as uuidv4 } from "uuid";

import * as self from "./contentUtils";
import {
  removeClassFromMatchingElements,
  removeStyleFromMatchingElements,
} from "../routes/BulkProcesses/ImportContent/content";
import { getLetter } from "./utils";

const addStyleToTableHeader = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("table")].forEach((table) => {
    const isPerspectiveTable =
      table.classList.contains("perspectiveTable") ||
      table.id === "perspectiveTable";
    if (isPerspectiveTable) {
      Object.assign(table.style, { borderStyle: "none" });
      [...table.querySelectorAll("tbody")].forEach((tableTh) => {
        tableTh.id = "perspectiveTableBody";
      });
      [...table.querySelectorAll("thead")].forEach((tableTh) => {
        tableTh.id = "perspectiveTableHead";
      });
    } else {
      [...table.querySelectorAll("thead tr th")].forEach((tableTh) => {
        Object.assign(tableTh.style, {
          textAlign: "center",
          verticalAlign: "bottom",
          fontWeight: "normal",
          borderStyle: "solid",
          borderWidth: "1px",
          padding: "3px",
        });
      });

      [...table.querySelectorAll("tbody tr th")].forEach((tableTh) => {
        Object.assign(tableTh.style, {
          textAlign: "left",
          verticalAlign: "middle",
          fontWeight: "normal",
          borderStyle: "solid",
          borderWidth: "1px",
          padding: "3px",
        });
      });
    }
  });
  return element.innerHTML;
};

const addXMLNSAttributesToMathML = (content) => {
  const element = createElement({ content });
  [...element.querySelectorAll("math")].map((el) => {
    if (!el.hasAttribute("xmlns"))
      el.setAttribute("xmlns", "http://www.w3.org/1998/Math/MathML");
    return true;
  });
  return element.innerHTML;
};

const adjustMathMLSpacing = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("mo")].map((el) => {
    if (
      (el.textContent === "." &&
        el.previousElementSibling?.tagName.toUpperCase() === "MN" &&
        el.nextElementSibling?.tagName.toUpperCase() === "MN") ||
      (el.textContent === "," &&
        el.nextElementSibling?.tagName.toUpperCase() === "MN" &&
        /^\d{3}$/.test(el.nextElementSibling?.textContent)) ||
      (/^(?:&#9651;|\u25b3)/.test(el.textContent) &&
        el.nextElementSibling?.tagName.toUpperCase() === "MI")
    ) {
      el.setAttribute("rspace", "0");
    }
    if (
      /^(?:~|&#8773;|\u2245)/.test(el.textContent) &&
      el.nextElementSibling?.tagName.toUpperCase() === "MO" &&
      /^(?:&#9651;|\u25b3)/.test(el.nextElementSibling?.textContent)
    ) {
      el.setAttribute("lspace", "0.5em");
    }
  });
  return element.innerHTML;
};

const adjustItalicizedText = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("i")].forEach((el) => {
    if (el.textContent) {
      const emElement = document.createElement("em");
      emElement.innerHTML = el.innerHTML;
      el.replaceWith(emElement);
    }
  });
  return element.innerHTML;
};

const adjustEMText = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("em")].forEach((el) => {
    if (el.textContent) {
      const iElement = document.createElement("i");
      iElement.innerHTML = el.innerHTML;
      el.replaceWith(iElement);
    }
  });
  return element.innerHTML;
};

const unwrapPlainTableCellParagraphs = (contentElement) => {
  Array.from(contentElement.querySelectorAll("td, th")).forEach((cell) => {
    const cellChildren = Array.from(cell.childNodes).filter(
      (node) =>
        node.nodeType !== Node.TEXT_NODE || node.nodeValue.trim() !== "",
    );
    const [onlyChild] = cellChildren;
    const isBareWrapperParagraph =
      cellChildren.length === 1 &&
      onlyChild.nodeType === Node.ELEMENT_NODE &&
      onlyChild.tagName === "P" &&
      onlyChild.attributes.length === 0;
    if (isBareWrapperParagraph) cell.innerHTML = onlyChild.innerHTML;
  });
};

const adjustTableFormattingForAPI = (content) => {
  const contentElement = document.createElement("div");
  contentElement.innerHTML = content;

  // CKEditor's Table + GeneralHtmlSupport can turn a cell's implicit
  // paragraph into a literal <p>, which breaks the cell's vertical-align
  // (CDE-17767). Strip it here so it never reaches storage.
  unwrapPlainTableCellParagraphs(contentElement);

  Array.from(contentElement.querySelectorAll('figure[class="table"]')).map(
    (el) => {
      const table = el.querySelector("table");
      const correspondingFigureWidth =
        el.style.width || el.getAttribute("style") || "";
      if (table && correspondingFigureWidth) {
        table.style.width = correspondingFigureWidth;
      }
    },
  );
  // Remove figures from around tables
  unwrapElements(`figure[class="table"]`)(contentElement);

  // Add styles to tables
  const tableStyles = {
    "border-collapse": "collapse",
    "border-spacing": "0px",
    "border-style": "solid",
    "border-width": "1px",
  };
  Array.from(contentElement.querySelectorAll(`table`)).map((el) => {
    Object.keys(tableStyles).map((style) => {
      if (!el.style.getPropertyValue(style))
        el.style.setProperty(style, tableStyles[style]);
    });
  });

  // Add styles to table data elements
  const tdStyles = {
    "border-style": "solid",
    "border-width": "1px",
    padding: "3px",
  };
  Array.from(contentElement.querySelectorAll("td")).map((el) => {
    Object.keys(tdStyles).map((style) => {
      if (!el.style.getPropertyValue(style))
        el.style.setProperty(style, tdStyles[style]);
    });
  });

  Array.from(contentElement.querySelectorAll("[style]")).map((el) => {
    if (!el.style.length) el.removeAttribute("style");
    el.getAttribute("style") &&
      el.setAttribute(
        "style",
        el.getAttribute("style").replace(/:\s+/g, ":").replace(/;\s+/g, ";"),
      );
  });

  return contentElement.innerHTML;
};

const adjustTableFormattingForUI = (content) => {
  const contentElement = document.createElement("div");
  contentElement.innerHTML = content;

  // Self-heal previously affected content (CDE-17767) as it's loaded, so a
  // resave clears the stray <p> instead of just hiding it in the editor.
  unwrapPlainTableCellParagraphs(contentElement);

  // Remove styles from tables and wrap them in figure elements
  const tableStyles = {
    "border-collapse": "collapse",
    "border-spacing": "0px",
    "border-style": "solid",
    "border-width": "1px",
  };
  Array.from(contentElement.querySelectorAll(`table`)).map((el) => {
    const figure = createElement({
      attributes: { class: "table" },
      element: "figure",
    });

    const tableWidth = el.style.width || el.getAttribute("style") || "";
    // Apply table width to the figure
    figure.style.width = tableWidth;
    el.style.removeProperty("width");

    el.before(figure);
    figure.appendChild(el);

    Object.keys(tableStyles).map((style) => {
      if (el.style.getPropertyValue(style) === tableStyles[style])
        el.style.removeProperty(style);
    });
  });

  // Add styles to table data elements
  const tdStyles = {
    "border-style": "solid",
    "border-width": "1px",
    padding: "3px",
  };
  Array.from(contentElement.querySelectorAll("td")).map((el) => {
    Object.keys(tdStyles).map((style) => {
      if (el.style.getPropertyValue(style) === tdStyles[style])
        el.style.removeProperty(style);
    });
  });

  Array.from(contentElement.querySelectorAll("[style]")).map((el) => {
    if (!el.style.length) return el.removeAttribute("style");
    el.setAttribute(
      "style",
      el.getAttribute("style").replace(/:\s+/g, ":").replace(/;\s+/g, ";"),
    );
  });

  return contentElement.innerHTML;
};

const applyTTSAccessibility = (type) => (obj) => {
  const normalizedContent = wrapPlainTextWithP(obj.content);
  const [initialContent, initialAccessibilityElements = []] =
    self.getTextToSpeechTags(type)({ ...obj, content: normalizedContent });

  const contentElement = self.createElement({ content: initialContent });

  const activeIds = new Set(
    initialAccessibilityElements.map((t) => t.identifier),
  );
  contentElement.querySelectorAll("span[id]").forEach((span) => {
    if (!activeIds.has(span.getAttribute("id"))) {
      span.replaceWith(...span.childNodes);
    }
  });

  const isEnglish = obj.contentArea === "English";
  return [...contentElement.querySelectorAll(".accessibility-content")].reduce(
    (acc, el) => {
      const [accessibilityElements, content, key] =
        applyTTSAccessibilityToContent(el)({ isEnglish });

      return _.pipe([
        _.set(key)(self.getCleanContent(content)),
        _.set(`${type}.accessibilityElements`)([
          ...acc[type].accessibilityElements,
          ...accessibilityElements,
        ]),
      ])({ ...acc });
    },
    { [type]: { ...obj, accessibilityElements: initialAccessibilityElements } },
  )[type];
};

const applyTTSAccessibilityToContent =
  (contentElement) =>
  (options = {}) => {
    const key = getContentLocation(contentElement);
    const tags = [...contentElement.querySelectorAll("li,p,td,th")].reduce(
      (acc, element) => {
        return [
          ...acc,
          ...applyTTSAccessibilityToRangesInElement(element)(options),
          ...applyTTSAccessibilityToElement(element),
        ];
      },
      [],
    );

    contentElement.querySelectorAll("p:empty").forEach((p) => p.remove());

    return [tags, contentElement.innerHTML, key];
  };

const applyTTSAccessibilityToElement = (element) => {
  if (
    !element.matches("td,th") ||
    element.matches(".ttsAccessibilityElement") ||
    element.closest("table.perspectiveTable")
  )
    return [];

  const data = {
    accessibilityFeatures: [
      {
        feature: "Text to Speech",
        text: element.textContent || "Blank",
        type: "Spoken",
      },
    ],
    inclusionOrders: [
      "Spoken, Text and Graphics : Default",
      "Spoken, Text and Graphics : On Demand",
    ],
  };

  const [tag] = createTTSAccessibilityTag({
    elements: [element],
    tag: data,
  });
  return [tag];
};

const applyTTSAccessibilityToRangesInElement =
  (element) =>
  (options = {}) => {
    if (options.isEnglish) {
      element.querySelectorAll("mark[data-uuid]").forEach((mark) => {
        if (mark.textContent.trim() === "*") {
          mark.textContent = "[*]";
        }
      });
    }

    let newTags = [];
    while (1) {
      const range = _.pipe([
        getNextTTSRange(options),
        getTrimmedRange,
        expandRangeToFindCommonNode,
      ])(element);
      if (
        range.startContainer.nodeName === "#document" ||
        range.endContainer.nodeName === "#document"
      )
        break;
      if (range.collapsed || !range.toString()) break;

      const rawText = range.toString();
      const BRACKETED_ASTERISK = /\[\*\]/g;
      const LONE_ASTERISK = /^(\s*)\*(\s*)$/;
      const spokenText = options.isEnglish
        ? rawText
            .replace(BRACKETED_ASTERISK, "asterisk")
            .replace(LONE_ASTERISK, "$1asterisk$2")
        : rawText;

      const data = {
        accessibilityFeatures: [
          {
            feature: "Text to Speech",
            text: spokenText,
            type: "Spoken",
          },
        ],
        inclusionOrders: [
          "Spoken, Text and Graphics : Default",
          "Spoken, Text and Graphics : On Demand",
        ],
      };

      const [tag] = createTTSAccessibilityTag({
        range,
        tag: data,
      });

      newTags = [...newTags, tag];
    }
    return newTags;
  };

// Convert UTF-8 characters to HTML entities
// Also, handle inconsistencies between browsers - e.g., some will interpret &#60; as <, some as &lt;
const convertUTF8ToHTMLEntities = (content) =>
  content
    .replace(/[\u00A0-\u9999]/gm, (i) => {
      return "&#" + i.charCodeAt(0) + ";";
    })
    .replaceAll(/&lt;/gm, "&#60;")
    .replaceAll(/&gt;/gm, "&#62;");

const convertSuggestionsForDisplay = (content) => {
  const types = {
    deletion: "del",
    insertion: "ins",
  };
  const element = createElement({ content });

  const filter = (node) => {
    // Only return nodes that are marks with a data-uuid attribute or
    // text nodes not already wrapped in highlights
    return self.isSugggestionElement(node) ||
      self.isTextNodeInElement("del,ins")(node) === null
      ? NodeFilter.FILTER_ACCEPT
      : NodeFilter.FILTER_SKIP;
  };

  const treeWalker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    filter,
  );

  let currentSuggestions = [];
  while (treeWalker.nextNode()) {
    const node = treeWalker.currentNode;

    if (node.nodeType !== Node.TEXT_NODE) {
      [...node.attributes]
        .filter((attr) => /^data-suggestion-(?:start|end)-/.test(attr.name))
        .forEach((attribute) => {
          switch (attribute.name) {
            case "data-suggestion-start-before":
              currentSuggestions = [
                ...currentSuggestions,
                ...attribute.value.split(","),
              ];
              break;
            case "data-suggestion-end-before":
              currentSuggestions = currentSuggestions.filter(
                (name) => !attribute.value.split(",").includes(name),
              );
              break;
            case "data-suggestion-start-after":
              const suggestionStartEl = createElement({
                element: "suggestion-start",
                attributes: { name: attribute.value },
              });
              node.after(suggestionStartEl);
              break;
            case "data-suggestion-end-after":
              const suggestionEndEl = createElement({
                element: "suggestion-end",
                attributes: { name: attribute.value },
              });
              node.after(suggestionEndEl);
              break;
            default:
              break;
          }
        });
      if (node.nodeName.toUpperCase() === "SUGGESTION-START") {
        currentSuggestions = [
          ...currentSuggestions,
          ...node.getAttribute("name").split(","),
        ];
      }
      if (node.nodeName.toUpperCase() === "SUGGESTION-END") {
        currentSuggestions = currentSuggestions.filter(
          (name) => !node.getAttribute("name").split(",").includes(name),
        );
      }
      continue;
    }
    if (node.nodeType === Node.TEXT_NODE && currentSuggestions.length) {
      if (!node.parentNode) continue;
      let index = -1;
      if (
        node.parentNode.hasAttribute("data-suggestion-end-before") ||
        node.parentNode.hasAttribute("data-suggestion-start-after")
      ) {
        if (currentSuggestions.length < 2) continue;
        index = -2;
      }

      element.removeAttribute("data-suggestion-start-before");
      element.removeAttribute("data-suggestion-end-after");

      const name = currentSuggestions.at(index);
      const [type] = name.split(":");
      if (node.parentElement.closest("math")) continue;
      const span = self.createElement({
        element: types[type],
      });
      node.parentNode.replaceChild(span, node);
      span.appendChild(node);
    }
  }
  return element.innerHTML;
};

const convertTasksToUI = (users) => (tasks) => {
  const userMap = new Map(users.map((user) => [user.id, user]));
  const sortTasks = (tasks) => (sortByKey) => {
    return tasks.sort((a, b) => {
      return new Date(a[sortByKey]) - new Date(b[sortByKey]);
    });
  };
  const separateTasks = tasks.reduce(
    (result, { completedDateTime, ...rest }) => {
      if (!!completedDateTime) {
        result.completed.push({ completedDateTime, ...rest });
      } else {
        result.incomplete.push(rest);
      }
      return result;
    },
    { completed: [], incomplete: [] },
  );

  const sortedCompletedTasks = sortTasks(separateTasks?.completed)(
    "completedDateTime",
  );

  const sortedIncompleteTasks = sortTasks(separateTasks?.incomplete)(
    "createdDateTime",
  );

  return [...sortedIncompleteTasks, ...sortedCompletedTasks].map(
    ({
      assignedUser,
      createdByUser,
      completedByUser,
      createdDateTime,
      completedDateTime,
      id,
      ...rest
    }) => {
      const { firstName = "", lastName = "" } = userMap.get(assignedUser) || {};
      return {
        ...rest,
        assignedUser: `${firstName} ${lastName}`,
        createdDateTime: `${new Date(createdDateTime).toLocaleString()}`,
        completedDateTime: !!completedDateTime
          ? `${new Date(completedDateTime).toLocaleDateString()}`
          : null,
        taskID: id,
      };
    },
  );
};

const createDynamicContentElements =
  (passage) =>
  (tag) =>
  ([element, labels]) => {
    const { dynamicTags = [], items = [] } = passage;

    // Insert placeholders for Insert Correct tags
    dynamicTags
      .filter((t) => {
        if (t.__typename !== "DynamicModifyContentTag") return;
        const range = getDynamicModifyContentTagRange(element)(t);
        return range.toString() === "";
      })
      .map(({ begin, end }) => {
        const placeholderElement = self.createElement({
          content: "[i]",
          element: "i",
        });
        const mark = element.querySelector(`mark[data-uuid="${begin}"]`);
        mark.after(placeholderElement);
      });

    const filter = (node) => {
      // Only return nodes that are marks with a data-uuid attribute or
      // text nodes not already wrapped in highlights
      return self.isBreakParagraphMarkElement(dynamicTags)(node) ||
        self.isLabelMarkElement(labels)(node) ||
        self.isModifyContentMarkElement(dynamicTags)(node) ||
        self.isTextNodeInElement(".breakParagraph,.modifyContent")(node) ===
          null
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    };

    const treeWalker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      filter,
    );

    let currentBreakParagraphTags = [];
    let currentModifyContentTags = [];
    let currentRemoveLabelsTags = [];
    while (treeWalker.nextNode()) {
      const node = treeWalker.currentNode;

      // If it's a <mark> tag, adjust current highlights, as needed
      if (node.nodeName.toUpperCase() === "MARK") {
        const uuid = node.getAttribute("data-uuid");

        currentBreakParagraphTags = [
          ...currentBreakParagraphTags,
          ...labels.reduce((acc, { begin, end }) => {
            if (begin !== uuid) return [...acc];
            const breakParagraphTag = dynamicTags.find(
              ({ __typename, point }) =>
                __typename === "DynamicBreakParagraphTag" && point === uuid,
            );
            if (!breakParagraphTag) return [...acc];
            return [
              ...acc,
              {
                begin,
                end,
                items: breakParagraphTag.items,
              },
            ];
          }, []),
        ].filter(({ end }) => end !== uuid);

        currentModifyContentTags = [
          ...currentModifyContentTags,
          ...dynamicTags.filter(
            ({ __typename, begin }) =>
              __typename === "DynamicModifyContentTag" && begin === uuid,
          ),
        ].filter(({ end }) => end !== uuid);

        currentRemoveLabelsTags = [
          ...currentRemoveLabelsTags,
          ...dynamicTags.filter(
            (o) =>
              o.__typename === "DynamicRemoveLabelsTag" &&
              o.labels.some((l) => l.begin === uuid),
          ),
        ].filter((o) => !o.labels.some((l) => l.end === uuid));
        continue;
      }

      // If it's a text node and there are current modifyContent tags, wrap the node in a <span> tag
      if (
        node.nodeType === Node.TEXT_NODE &&
        (currentModifyContentTags.length || node.textContent === "[i]")
      ) {
        if (!node.parentNode) continue;

        const active =
          tag?.__typename === "DynamicModifyContentTag" &&
          (currentModifyContentTags.some(
            ({ begin, end }) => tag.begin === begin && tag.end === end,
          ) ||
            node.parentElement.closest(`mark[data-uuid="${tag.begin}"]`)) &&
          "active";
        const underline =
          currentModifyContentTags.some(({ underline }) => underline) &&
          "underline";
        const className = [active, "modifyContent", underline]
          .filter(Boolean)
          .join(" ");

        const span = self.createElement({
          attributes: {
            className,
            title: self.getItemNamesForTags(items)(currentModifyContentTags),
          },
          element: "span",
        });
        node.parentNode.replaceChild(span, node);
        span.appendChild(node);
      }

      // If it's a text node and there are current removeLabels tags, wrap the node in a <span> tag
      if (node.nodeType === Node.TEXT_NODE && currentRemoveLabelsTags.length) {
        if (!node.parentNode) continue;

        const active =
          tag &&
          tag.__typename === "DynamicRemoveLabelsTag" &&
          currentRemoveLabelsTags.some((o) =>
            o.labels.some(({ begin, end }) =>
              tag.labels.some((l) => l.begin === begin && l.end === end),
            ),
          ) &&
          "active";
        const className = [active, "removeLabels"].filter(Boolean).join(" ");

        const span = self.createElement({
          attributes: {
            className,
            title: self.getItemNamesForTags(items)(currentRemoveLabelsTags),
          },
          element: "span",
        });
        node.parentNode.replaceChild(span, node);
        span.appendChild(node);
      }

      const wrappingBreakParagraphTags = dynamicTags.filter(
        (o) =>
          o.__typename === "DynamicBreakParagraphTag" &&
          node.parentNode.closest(`mark[data-uuid="${o.point}"]`),
      );

      // If it's a text node and there is a current breakParagraph tag, wrap the node in a <span> tag
      if (
        node.nodeType === Node.TEXT_NODE &&
        (currentBreakParagraphTags.length || wrappingBreakParagraphTags.length)
      ) {
        if (!node.parentNode) continue;

        const active =
          tag &&
          tag.__typename === "DynamicBreakParagraphTag" &&
          (currentBreakParagraphTags.some(({ begin }) => tag.point === begin) ||
            wrappingBreakParagraphTags.some(
              ({ point }) => tag.point === point,
            )) &&
          "active";

        const className = [active, "breakParagraph"].filter(Boolean).join(" ");

        const span = self.createElement({
          attributes: {
            className,
            title: self.getItemNamesForTags(items)([
              ...currentBreakParagraphTags,
              ...wrappingBreakParagraphTags,
            ]),
          },
          element: "span",
        });
        node.parentNode.replaceChild(span, node);
        span.appendChild(node);
      }
    }

    // NOTE: Redefining the labels because they were collapsed after node replacement
    return [element, labels];
  };

const createElement = ({
  attributes,
  content = "",
  element: type = "div",
  fragment,
}) => {
  const element = document.createElement(type);
  element.innerHTML = content;
  fragment && element.appendChild(fragment.cloneNode(true));
  attributes &&
    Object.keys(attributes).map((key) => {
      const attributeName = key === "className" ? "class" : key;
      element.setAttribute(attributeName, attributes[key]);
    });
  return element;
};

const createHighlightElements =
  (passage) =>
  ([element, labels]) => {
    const { highlights = [], items = [] } = passage;

    const filter = (node) => {
      // Only return nodes that are marks with a data-uuid attribute or
      // text nodes not already wrapped in highlights
      return self.isHighlightMarkElement(highlights)(node) ||
        self.isTextNodeInElement(".highlight")(node) === null
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    };

    const treeWalker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      filter,
    );

    let currentHighlights = [];
    while (treeWalker.nextNode()) {
      const node = treeWalker.currentNode;

      // If it's a <mark> tag, adjust current highlights, as needed
      if (node.nodeName.toUpperCase() === "MARK") {
        const uuid = node.getAttribute("data-uuid");

        currentHighlights = [
          ...currentHighlights,
          ...highlights.filter(({ begin }) => begin === uuid),
        ].filter(({ end }) => end !== uuid);
        continue;
      }

      // If it's a text node and there are current highlights, wrap the node in a <span> tag
      if (node.nodeType === Node.TEXT_NODE && currentHighlights.length) {
        if (!node.parentNode) continue;

        const span = self.createElement({
          attributes: {
            className: "highlight",
            title: self.getItemNamesForTags(items)(currentHighlights),
          },
          element: "span",
        });
        node.parentNode.replaceChild(span, node);
        span.appendChild(node);
      }
    }
    return [element, labels];
  };

const createLabelElements = (element) => {
  let ranges = [];

  const filter = (node) => {
    // Only return text nodes that match the label pattern
    return node.nodeType === Node.TEXT_NODE &&
      /\[[0-9A-Z]+\]/.test(node.textContent)
      ? NodeFilter.FILTER_ACCEPT
      : NodeFilter.FILTER_SKIP;
  };
  const treeWalker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    filter,
  );

  while (treeWalker.nextNode()) {
    const node = treeWalker.currentNode;

    const re = /\[[0-9A-Z]+\]/dgm;
    let results;
    while ((results = re.exec(node.textContent)) !== null) {
      const range = new Range();
      range.setStart(node, results.indices[0][0]);
      range.setEnd(node, results.indices[0][1]);
      ranges = [...ranges, range.cloneRange()];
    }
  }

  const marks = [...element.querySelectorAll(`mark[data-uuid]`)];

  const labels = ranges.map((range) => {
    const check = new Range();
    const type = /^\s*\[[0-9A-Z]+\]\s*$/.test(
      getSurroundingElements(element)(range)("p")[0]?.textContent,
    )
      ? "paragraph"
      : "sentence";

    if (type === "paragraph") {
      if (range.startOffset !== 0) range.setStart(range.startContainer, 0);
      if (range.endOffset !== range.endContainer.nodeValue.length)
        range.setEnd(range.endContainer, range.endContainer.nodeValue.length);
    }

    let beginMark, endMark;

    // If the range starts at the start of the text node, see if there is an adjacent mark tag
    if (range.startOffset === 0) {
      beginMark = _.findLast((el) => {
        // If the mark is not in the same paragraph or before the node
        if (
          el.closest("p") !== range.startContainer.parentNode.closest("p") ||
          !(
            el.compareDocumentPosition(range.startContainer) &
            Node.DOCUMENT_POSITION_FOLLOWING
          )
        )
          return;
        check.setStartAfter(el);
        check.setEndBefore(range.startContainer);
        return !check.toString().length;
      })(marks);
    }

    // If we didn't find an adjacent mark, create and insert one
    if (!beginMark) {
      beginMark = self.createElement({
        attributes: { "data-uuid": uuidv4() },
        element: "mark",
      });
      range.insertNode(beginMark);
    }

    // If the range ends at the end of the text node, see if there is an adjacent mark tag
    if (range.endOffset === range.endContainer.nodeValue.length) {
      endMark = marks.find((el) => {
        // If the mark is not before the node
        if (
          !(
            el.compareDocumentPosition(range.endContainer) &
            Node.DOCUMENT_POSITION_PRECEDING
          ) ||
          el.closest("p") !== range.endContainer.parentElement.closest("p")
        )
          return;
        check.setStartAfter(range.endContainer);
        check.setEndBefore(el);
        return !check.toString().length;
      });
    }

    // If we didn't find an adjacent mark, create and insert one at the end of the range
    if (!endMark) {
      endMark = self.createElement({
        attributes: { "data-uuid": uuidv4() },
        element: "mark",
      });
      range.collapse();
      range.insertNode(endMark);
    }

    const labelRange = new Range();
    labelRange.setStartAfter(beginMark);
    labelRange.setEndBefore(endMark);
    return {
      begin: beginMark.getAttribute("data-uuid"),
      content: labelRange.toString() || "",
      end: endMark.getAttribute("data-uuid"),
      type,
    };
  });
  return [element, labels];
};

const createParagraphUUIDs = (content) => {
  self
    .getOrderedElements(content)("p")
    .filter((element) => {
      return !/^\s*\[[0-9A-Z]+\]\s*$/.test(element.textContent);
    })
    .map((element) => {
      if (!element.hasAttribute("data-uuid")) {
        element.setAttribute("data-uuid", uuidv4());
      }
    });
  return content;
};

const createSRAccessibilityTag = ({ elements, label, range, tag }) => {
  const identifier = uuidv4();
  let contentElement;
  if (elements?.length) {
    const hiddenDiv = self.createElement({
      attributes: {
        className: "qti-visually-hidden srTransform",
        id: identifier,
      },
      content: tag.content,
    });
    elements.map((element) => {
      const isImage = label === "Image";
      element.setAttribute("aria-describedby", identifier);
      if (isImage) {
        element
          .querySelector("img")
          .setAttribute("aria-describedby", identifier);
      }
      element.classList.add("srTransform", "srAccessibilityElement");
    });

    contentElement = elements[0].closest(".accessibility-content");
    contentElement.appendChild(hiddenDiv);
  }
  if (range) {
    const content = self.createElement({ content: tag.content });
    [...content.querySelectorAll("*")].filter(isBlockElement).map((el) => {
      const childNodes = [...el.querySelectorAll("*")];
      el.replaceWith(...childNodes);
    });
    const hiddenSpan = self.createElement({
      attributes: {
        className: "qti-visually-hidden srTransform",
        id: identifier,
      },
      content: content.innerHTML,
      element: "span",
    });
    const wrappingSpan = self.createElement({
      attributes: {
        className: "srTransform srAccessibilityElement",
        "aria-describedby": identifier,
      },
      element: "span",
    });
    range.surroundContents(wrappingSpan);
    range.collapse();
    range.setStartAfter(wrappingSpan);
    range.insertNode(hiddenSpan);

    contentElement = range.startContainer.parentElement.closest(
      ".accessibility-content",
    );
  }
  const id = contentElement.getAttribute("id");
  const type = id.split("-")[0];
  let contentLocation = `${type}.content`;
  const re = new RegExp(
    `^${type}-interaction-(?<index>\\d+)-foil-(?<i>\\d+)-content$`,
  );
  if (re.test(id)) {
    const { groups } = id.match(re);
    contentLocation = `${type}.interactions[${groups.index}].foils[${groups.i}].content`;
  }
  return [contentElement, contentLocation];
};

const createTTSAccessibilityTag = ({ elements, label, range, tag }) => {
  const identifier = uuidv4();
  let contentElement;
  if (elements && elements.length) {
    elements.map((element) => {
      const isImage = label === "Image";
      element.classList.add("ttsAccessibilityElement");
      element.setAttribute("id", identifier);
      if (isImage) {
        element.querySelector("img").classList.add("ttsAccessibilityElement");
        element.querySelector("img").setAttribute("id", identifier);
      }
    });
    contentElement = elements[0].closest(`.accessibility-content`);
  }
  if (range) {
    const span = self.createElement({
      attributes: { className: "ttsAccessibilityElement", id: identifier },
      element: "span",
    });
    const mathElement = range.startContainer.parentElement.closest(`math`);
    if (mathElement) {
      mathElement.parentNode.replaceChild(span, mathElement);
      span.appendChild(mathElement);
    } else {
      span.appendChild(range.extractContents());
    }

    range.insertNode(span);
    contentElement = range.startContainer.parentElement.closest(
      `.accessibility-content`,
    );
  }
  const src = ["mp3", "ogg", "xml"].map((ext) => `${identifier}.${ext}`);
  const accessibilityFeatures = tag.accessibilityFeatures.map((af) => ({
    ...af,
    src,
  }));
  const contentLocation = getContentLocation(contentElement);
  return [
    { ...tag, accessibilityFeatures, identifier },
    contentElement,
    contentLocation,
  ];
};

const deleteDynamicTags = (passage) => (contentElement) => (dynamicTags) => {
  dynamicTags.map((tag) => {
    // If this is an "Insert Correct" tag, remove the placeholder text
    if (tag.__typename === "DynamicModifyContentTag") {
      const range = new Range();

      range.setStartAfter(
        contentElement.querySelector(`mark[data-uuid="${tag.begin}"]`),
      );
      range.setEndBefore(
        contentElement.querySelector(`mark[data-uuid="${tag.end}"]`),
      );
      if (range.toString() === "[i]") {
        range.deleteContents();
      }
      if (tag.underline) {
        const span = createElement({
          attributes: { className: "passage-underline" },
          element: "span",
        });
        range.surroundContents(span);
      }
    }
  });

  const tags = {
    dynamicTags: passage.dynamicTags.filter(
      (o) => !dynamicTags.some(_.matches(o)),
    ),
    highlights: passage.highlights,
  };

  return [
    _.pipe([removeUnnecessaryMarkTags(tags), getCleanContent])(contentElement),
    tags,
  ];
};

const deleteHighlights = (passage) => (contentElement) => (highlights) => {
  const dynamicTagsToDelete = getAssociatedDynamicTags(passage)(highlights);
  return deleteDynamicTags({
    dynamicTags: passage.dynamicTags,
    highlights: passage.highlights.filter(
      (o) => !highlights.some(_.matches(o)),
    ),
  })(contentElement)(dynamicTagsToDelete);
};

const deleteScreenReaderTags =
  (type) =>
  ({ content = "", ...obj }) =>
  (identifiers) => {
    const newContent = deleteScreenReaderTagsFromContent(content)(identifiers);
    let interactions;
    if (type === "item") {
      interactions = obj.interactions.map((interaction) => {
        if (interaction.__typename !== "ChoiceInteraction")
          return { ...interaction };
        const foils = interaction.foils.map((foil) => {
          return {
            ...foil,
            content: deleteScreenReaderTagsFromContent(foil.content)(
              identifiers,
            ),
          };
        });
        return { ...interaction, foils };
      });
    }
    return type === "item"
      ? { ...obj, content: newContent, interactions }
      : { ...obj, content: newContent };
  };

const deleteScreenReaderTagsFromContent = (content) => (identifiers) => {
  const element = self.createElement({ content });
  identifiers.map((identifier) => {
    const srElements = [...element.querySelectorAll(`[id="${identifier}"]`)];
    if (!srElements.length) return;
    srElements.map((el) => {
      el.remove();
    });
    const elementsWSRTags = [
      ...element.querySelectorAll(`[aria-describedby="${identifier}"]`),
    ];
    if (!elementsWSRTags.length) return;
    elementsWSRTags.map((el) => {
      switch (el.tagName.toUpperCase()) {
        case "SPAN":
          el.replaceWith(...el.childNodes);
          break;
        case "IMG":
        case "MATH":
          el.removeAttribute("aria-describedby");
          el.classList.remove(
            "srAccessibilityElement",
            "srTransform",
            "active",
          );
          break;
      }
    });
  });
  return element.innerHTML;
};

const deleteTextToSpeechTags =
  (type) =>
  ({ content = "", ...obj }) =>
  (identifiers) => {
    const newContent = deleteTextToSpeechTagsFromContent(content)(identifiers);

    const accessibilityElements = obj.accessibilityElements.filter(
      ({ identifier }) => !identifiers.includes(identifier),
    );

    let interactions;
    if (type === "item") {
      interactions = obj.interactions.map((interaction) => {
        if (interaction.__typename !== "ChoiceInteraction")
          return { ...interaction };
        const foils = interaction.foils.map((foil) => {
          return {
            ...foil,
            content: deleteTextToSpeechTagsFromContent(foil.content)(
              identifiers,
            ),
          };
        });
        return { ...interaction, foils };
      });
    }
    return type === "item"
      ? { ...obj, content: newContent, interactions, accessibilityElements }
      : { ...obj, content: newContent, accessibilityElements };
  };

const deleteTextToSpeechTagsFromContent = (content) => (identifiers) => {
  const element = self.createElement({ content });
  identifiers.map((identifier) => {
    const elements = [...element.querySelectorAll(`[id="${identifier}"]`)];
    if (!elements.length) return;
    elements.map((el) => {
      switch (el.tagName.toUpperCase()) {
        case "SPAN":
          el.replaceWith(...el.childNodes);
          break;
        case "IMG":
        case "MATH":
        case "P":
        case "TABLE":
        case "TD":
        case "TH":
        case "TR":
          el.removeAttribute("id");
          el.classList.remove("ttsAccessibilityElement", "active");
          break;
      }
    });
  });
  return element.innerHTML;
};

const editContent = (content) => (data) => {
  const element = self.createElement({ content });
  const srElement = element.querySelector(`[id="${data.identifier}"]`);
  if (!srElement) return element.innerHTML;
  srElement.textContent = data.content;
  return element.innerHTML;
};

const editScreenReaderTagContent =
  (type) =>
  ({ content = "", ...obj }) =>
  (data) => {
    const newContent = editContent(content)(data);
    let interactions;
    if (type === "item") {
      interactions = obj.interactions.map((interaction) => {
        if (interaction.__typename !== "ChoiceInteraction")
          return { ...interaction };
        const foils = interaction.foils.map((foil) => {
          if (!foil.content.includes(data.identifier)) return { ...foil };
          return {
            ...foil,
            content: editContent(foil.content)(data),
          };
        });
        return { ...interaction, foils };
      });
    }
    return {
      ...obj,
      content: newContent,
      ...(type === "item" && { interactions }),
    };
  };

// Encode MathML tags so that they will display correctly (and be interpreted correctly by MathJax)
const encodeMathMLTags = (content) => {
  // NOTE: Performing this action through the DOM results in `open="&amp;lt;"` and `close="&amp;gt;"`
  const mfencedAngleBracketRE =
    /<mfenced([^>]+?)(open|close)="(&[gl]t;|[<>])"/gim;

  function replaceMFencedAngleBrackets(match, p1, p2, p3) {
    let code = p3;
    switch (p3) {
      case "<":
      case "&lt;":
        code = "&#60;";
        break;
      case ">":
      case "&gt;":
        code = "&#62;";
        break;
    }
    return `<mfenced${p1}${p2}="${code}"`;
  }

  while (mfencedAngleBracketRE.test(content)) {
    content = content.replace(
      mfencedAngleBracketRE,
      replaceMFencedAngleBrackets,
    );
  }

  const mfencedAttributeOrderRE =
    /<mfenced([^>]+?)close="([^"]+)"([^>]+?)open="([^"]+)"/gim;

  function updateAttributeOrder(match, p1, p2, p3, p4) {
    return `<mfenced${p1}open="${p4}"${p3}close="${p2}"`;
  }

  while (mfencedAttributeOrderRE.test(content)) {
    content = content.replace(mfencedAttributeOrderRE, updateAttributeOrder);
  }
  return content;
};

const expandRangeEnd = (range) => {
  // If the endContainer is a text node
  if (range.endContainer.nodeType === Node.TEXT_NODE) {
    // If we're not at the end of the node
    if (range.endOffset < range.endContainer.length) {
      // Split the text node at the end of the range
      range.endContainer.splitText(range.endOffset);
    }

    // If the parent node is not a block-level element
    if (!isBlockElement(range.endContainer.parentNode)) {
      // Clone the parent node (to preserve attributes)
      const clone = range.endContainer.parentNode.cloneNode();

      // Get the index of the endContainer node in the parent
      const index =
        [...range.endContainer.parentNode.childNodes].findIndex((n) =>
          range.endContainer.isSameNode(n),
        ) + 1;

      // Remove all childNodes of the parent node (after the endContainer) and append them to the clone
      while (range.endContainer.parentNode.childNodes[index]) {
        const childNode = range.endContainer.parentNode.removeChild(
          range.endContainer.parentNode.childNodes[index],
        );
        clone.appendChild(childNode);
      }

      // If the clone is not empty
      if (clone.childNodes.length) {
        // Insert the clone after the parent
        range.endContainer.parentNode.after(clone);
      }

      // If the parent's nextSibling exists and is a text node
      if (
        range.endContainer.parentNode.nextSibling &&
        range.endContainer.parentNode.nextSibling.nodeType === Node.TEXT_NODE
      ) {
        // Set the end of the range to the beginning of that node
        range.setEnd(range.endContainer.parentNode.nextSibling, 0);
      }

      // If the parent's nextSibling does not exist, or is not a text node
      else {
        // Insert an empty text node after the parent (to be the new endContainer)
        const endTextNode = document.createTextNode("");
        range.endContainer.parentNode.after(endTextNode);

        // Set the end of the range to the new text node
        range.setEnd(endTextNode, 0);
      }
    }
  }

  // If the endContainer is not a block-level element
  if (!isBlockElement(range.endContainer)) {
    // If there is an element after the endContainer
    if (range.endContainer.nextSibling) {
      // Cycle through the next siblings
      while (range.endContainer.nextSibling) {
        // If the endContainer is not a text node
        if (range.endContainer.nodeType !== Node.TEXT_NODE) {
          // Set the end of the range after the current endContainer
          range.setEndAfter(
            range.endContainer.nextSibling.textContent === "" ||
              !self.isVisible(range.endContainer.nextSibling)
              ? range.endContainer.nextSibling
              : range.endContainer,
          );
        }

        // If the endContainer is a text node, the offset is the length of the endContainer, and the sibling is empty
        else if (
          range.endOffset === range.endContainer.length &&
          (range.endContainer.nextSibling.textContent === "" ||
            !self.isVisible(range.endContainer.nextSibling))
        ) {
          // If the endContainer is empty
          if (range.endContainer.length === 0) {
            // Move the sibling inside the selection
            range.endContainer.before(range.endContainer.nextSibling);
          }

          // If the endContainer is not empty
          else {
            // Insert an empty text node after the sibling (to be the new endContainer)
            const textNode = window.document.createTextNode("");
            range.endContainer.nextSibling.after(textNode);

            // Set the end of the range to the new text node
            range.setEnd(textNode, 0);
          }
        }

        // If the endContainer is a text node, but the offset is not the length of the endContainer or the sibling is not empty
        else {
          break;
        }
      }
    }

    // If there isn't an element after the endContainer and we're not in a text node
    else if (range.endContainer.nodeType !== Node.TEXT_NODE) {
      // Set the end of the range after the current endContainer
      range.setEndAfter(range.endContainer);
    }
  }

  return range;
};

const expandRangeStart = (range) => {
  // If the startContainer is a text node
  if (range.startContainer.nodeType === Node.TEXT_NODE) {
    // If we're not at the beginning of the node
    if (range.startOffset > 0) {
      // Split the text node at the beginning of the range
      range.startContainer.splitText(range.startOffset);

      // Set the start of the range to the next sibling (the portion after the split)
      range.setStart(range.startContainer.nextSibling, 0);
    }

    // If the parent node is not a block-level element
    if (!isBlockElement(range.startContainer.parentNode)) {
      // Clone the parent node (to preserve attributes)
      const clone = range.startContainer.parentNode.cloneNode();

      // Remove all childNodes of the parent node (before the startContainer) and append them to the clone
      let node = range.startContainer.parentNode.childNodes[0];
      while (!node.isSameNode(range.startContainer)) {
        clone.appendChild(range.startContainer.parentNode.removeChild(node));
        node = range.startContainer.parentNode.childNodes[0];
      }

      // If the clone is not empty
      if (clone.childNodes.length) {
        // Insert the clone before the parent
        range.startContainer.parentNode.before(clone);
      }

      // If the parent's previousSibling exists and is a text node
      if (
        range.startContainer.parentNode.previousSibling &&
        range.startContainer.parentNode.previousSibling.nodeType ===
          Node.TEXT_NODE
      ) {
        // Set the beginning of the range to the end of that node
        range.setStart(
          range.startContainer.parentNode.previousSibling,
          range.startContainer.parentNode.previousSibling.length,
        );
      }

      // If the parent's previousSibling does not exist, or is not a text node
      else {
        // Insert an empty text node before the parent (to be the new startContainer)
        const textNode = document.createTextNode("");
        range.startContainer.parentNode.before(textNode);

        // Set the start of the range to the new text node
        range.setStart(textNode, 0);
      }
    }
  }

  // If the startContainer is not a block-level element
  if (!isBlockElement(range.startContainer)) {
    // If there is an element before the startContainer
    if (range.startContainer.previousSibling) {
      // Cycle through the previous siblings
      while (range.startContainer.previousSibling) {
        // If the startContainer is not a text node
        if (range.startContainer.nodeType !== Node.TEXT_NODE) {
          // Set the start of the range after the current startContainer
          range.setStartBefore(
            range.startContainer.previousSibling.textContent === "" ||
              !self.isVisible(range.startContainer.previousSibling)
              ? range.startContainer.previousSibling
              : range.startContainer,
          );
        }

        // If the startContainer is a text node, the offset is 0, and the sibling is empty
        else if (
          range.startOffset === 0 &&
          (range.startContainer.previousSibling.textContent === "" ||
            !self.isVisible(range.startContainer.previousSibling))
        ) {
          // If the startContainer is empty
          if (range.startContainer.length === 0) {
            // Move the sibling inside the selection
            range.startContainer.after(range.startContainer.previousSibling);
          }

          // If the startContainer is not empty
          else {
            // Insert an empty text node before the sibling (to be the new startContainer)
            const textNode = document.createTextNode("");
            range.startContainer.previousSibling.before(textNode);

            // Set the start of the range to the new text node
            range.setStart(textNode, 0);
          }
        }

        // If the startContainer is a text node, but the offset is not 0 or the sibling is not empty
        else {
          break;
        }
      }
    }

    // If there isn't an element before the startContainer and we're not in a text node
    else if (range.startContainer.nodeType !== Node.TEXT_NODE) {
      // Set the end of the range before the current startContainer
      range.setStartBefore(range.startContainer);
    }
  }

  return range;
};

// Find the outermost common node possible given a particular range
const expandRangeToFindCommonNode = (initialRange) => {
  const range = initialRange.cloneRange();

  // Bypass if the range starts and ends in the same container
  if (range.startContainer === range.endContainer) {
    return range;
  }

  // Find the common ancestor node and normalize to a non-text node
  const ancestorNode =
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentNode
      : range.commonAncestorContainer;

  // Find the levels between the current start/end and the common ancestor node
  let startParents = parentsUntil(ancestorNode)(range.startContainer);
  let endParents = parentsUntil(ancestorNode)(range.endContainer);

  // Bypass if any of the ancestor nodes are block level elements (as we don't span blocks)
  if (startParents.some(isBlockElement) || endParents.some(isBlockElement)) {
    return range;
  }

  let previousStartContainerParentNode =
    range.startContainer.parentNode.cloneNode(true);
  let previousEndContainerParentNode =
    range.endContainer.parentNode.cloneNode(true);

  // If the startContainer is nested
  while (
    startParents.length ||
    (range.startContainer.nodeType === Node.TEXT_NODE &&
      range.startContainer.parentNode !== ancestorNode) ||
    (range.startContainer.nodeType !== Node.TEXT_NODE &&
      range.startContainer !== ancestorNode)
  ) {
    // Adjust the start of the range
    self.expandRangeStart(range);

    // If the startContainer didn't change, break out of the loop
    if (
      range.startContainer.parentNode.isEqualNode(
        previousStartContainerParentNode,
      )
    ) {
      break;
    }

    // Get the new element hierarchy
    previousStartContainerParentNode =
      range.startContainer.parentNode.cloneNode(true);
    startParents = parentsUntil(ancestorNode)(range.startContainer);
  }

  // If the endContainer is nested
  while (
    endParents.length ||
    (range.endContainer.nodeType === Node.TEXT_NODE &&
      range.endContainer.parentNode !== ancestorNode) ||
    (range.endContainer.nodeType !== Node.TEXT_NODE &&
      range.endContainer !== ancestorNode)
  ) {
    // Adjust the end of the range
    self.expandRangeEnd(range);

    // If the endContainer didn't change, break out of the loop
    if (
      range.endContainer.parentNode.isEqualNode(previousEndContainerParentNode)
    ) {
      break;
    }

    // Get the new element hierarchy
    previousEndContainerParentNode =
      range.endContainer.parentNode.cloneNode(true);
    endParents = parentsUntil(ancestorNode)(range.endContainer);
  }

  return range;
};

const findFirstMatchingTextNode = (re) => (node) => {
  if (!node) return node;

  // If the node doesn't match the regular expression, return null
  if (!re.test(node.textContent)) return null;

  // If the node is a text node, return it
  if (node.nodeType === Node.TEXT_NODE) return node;

  // If the node is an element, find the first matching node
  if (node.nodeType === Node.ELEMENT_NODE)
    return [...node.childNodes].find(findFirstMatchingTextNode(re));
};

const findLastMatchingTextNode = (re) => (node) => {
  if (!node) return node;

  // If the node doesn't match the regular expression, return null
  if (!re.test(node.textContent)) return null;

  // If the node is a text node, return it
  if (node.nodeType === Node.TEXT_NODE) return node;

  // If the node is an element, find the last matching node
  if (node.nodeType === Node.ELEMENT_NODE)
    return [...node.childNodes].reverse().find(findLastMatchingTextNode(re));
};

const getAssociatedDynamicTags = (passage) => (highlights) => {
  // Find highlights that are associated with dynamic tags and remove the dynamic tags
  return passage.dynamicTags.filter(
    (tag) =>
      (tag.__typename === "DynamicModifyContentTag" &&
        tag.highlight &&
        highlights.some(
          _.matches({
            __typename: "Highlight",
            begin: tag.begin,
            end: tag.end,
          }),
        )) ||
      (tag.__typename === "DynamicBreakParagraphTag" &&
        highlights.some(
          _.matches({ __typename: "ItemPointReference", point: tag.point }),
        )),
  );
};

/*
  RIMS character count processing filters out the following for items:
  cbt ? "//qti:span[@class=\"PBT\"]" : "//qti:span[@class=\"CBT\"]",
  cbt ? "//qti:span[@title=\"PBT Text\"]" : "//qti:span[@title=\"CBT Text\"]",
  "//qti:p[@class=\"boxed_directions_stem\"]",
  "//qti:p[@class=\"paired_passage_instruction\"]",
  "//qti:p[@class=\"prompt\"]",
  "//qti:p[@class=\"stimulus_citation\"]",
  "//qti:span[@class=\"figure_labels\"]",
  "//qti:table/qti:caption",
  "//qti:img",
  "//text()[normalize-space(.) = \"\"]"

  ...and the following for passages:
  cbt ? "//qti:span[@class=\"PBT\"]" : "//qti:span[@class=\"CBT\"]",
  cbt ? "//qti:span[@title=\"PBT Text\"]" : "//qti:span[@title=\"CBT Text\"]",
  "//qti:div[@class=\"passage_head\"]",
  "//qti:p[@class=\"passage_head\"]",
  "//qti:div[@class=\"stimulus_head\"]",
  "//qti:p[@class=\"stimulus_head\"]",
  "//qti:div[@class=\"stimulus_head_italic\"]",
  "//qti:p[@class=\"stimulus_head_italic\"]",
  "//qti:div[@class=\"stimulus_head_essay\"]",
  "//qti:p[@class=\"stimulus_head_essay\"]",
  "//qti:div[@class=\"stimulus_citation\"]",
  "//qti:p[@class=\"stimulus_citation\"]",
  "//qti:div[@class=\"advance_organizer\"]",
  "//qti:p[@class=\"advance_organizer\"]",
  "//qti:div[@class=\"boxed_directions_stimulus\"]",
  "//qti:p[@class=\"boxed_directions_stimulus\"]",
  "//qti:div[@class=\"stimulus_paragraph_number\"]",
  "//qti:p[@class=\"stimulus_paragraph_number\"]",
  "//qti:div[@class=\"paired_passage_head\"]",
  "//qti:p[@class=\"paired_passage_head\"]",
  "//qti:div[@class=\"paired_passage_instruction\"]",
  "//qti:p[@class=\"paired_passage_instruction\"]",
  "//qti:span[@class=\"x_act_item_point_reference\"]",
  "//qti:span[@class=\"figure_labels\"]",
  "//qti:span[@class=\"crct\"]",
  "//qti:span[@class=\"scpsp\"]",
  "//qti:span[@class=\"add\"]",
  "//qti:span[@class=\"dest\"]",
  "//qti:table/qti:caption",
  "//qti:beginhl",
  "//qti:endhl",
  "//qti:img",
  "//text()[normalize-space(.) = \"\"]");

  Since there is some overlap between them, and the item/passage classes that
  don't overlap aren't valid for passages/items (respectively), I've kept them
  in the same function.
*/
const getCharacterCount =
  (type) =>
  ({
    choiceInteractions = [],
    content = "",
    gapMatchInteractions = [],
    inlineChoiceInteractions = [],
    matchInteractions = [],
    orderInteractions = [],
  }) => {
    // First, get all of the character content in one place
    const element = self.createElement({ content });

    // Add content for appropriate interactions
    choiceInteractions?.map((interaction) => {
      interaction.foils.map(({ content }) => {
        element.appendChild(self.createElement({ content }));
      });
    });
    [...gapMatchInteractions, ...matchInteractions, ...orderInteractions].map(
      (interaction) => {
        interaction.elements.map(({ content }) => {
          element.appendChild(self.createElement({ content }));
        });
      },
    );
    inlineChoiceInteractions?.map((interaction) => {
      interaction.choices.map(({ text }) => {
        element.appendChild(self.createElement({ content: text }));
      });
    });
    [...element.querySelectorAll("*")].map((el) => {
      if (el.nodeType === Node.ELEMENT_NODE) {
        if (
          el.matches(
            [
              "section[data-interaction-name],slot[data-interaction-name]",
              type === "cbt" ? ".PBT" : null,
              type === "pbt" ? ".CBT" : null,
              ".advance_organizer",
              ".boxed_directions_stem",
              ".boxed_directions_stimulus",
              ".paired_passage_head",
              ".paired_passage_instruction",
              ".passage_head",
              ".prompt",
              ".stimulus_citation",
              ".stimulus_head",
              ".stimulus_head_essay",
              ".stimulus_head_italic",
              ".stimulus_paragraph_number",
              ".cite",
              ".figure_labels",
              "table > caption",
              "img",
              "mark",
            ]
              .filter(Boolean)
              .join(","),
          ) ||
          (el.matches("div,p") &&
            /^\s*figure\s+\d+[.:]*\s*$/i.test(el.textContent))
        )
          el.remove();
      }
      if (el.nodeType === Node.TEXT_NODE) {
        if (/^[ \t\r\n]+$/.test(el.textContent)) el.remove();
      }
    });
    return element.textContent.length;
  };

const getCleanContent = (element) => {
  if (!element) return "";
  return _.pipe([
    (element) =>
      self.createElement({
        content: typeof element === "string" ? element : element?.innerHTML,
      }),
    self.unwrapElements(".highlight"),
    self.unwrapElements(".modifyContent"),
    self.unwrapElements(".removeLabels"),
    self.unwrapElements(".breakParagraph"),
    self.unwrapElements(".accessibility-image"),
    self.removeInsertCorrectTextPlaceholders,
    (element) => element.innerHTML,
    removeClassFromMatchingElements(
      ".srAccessibilityElement.active,.ttsAccessibilityElement.active",
    )("active"),
    removeClassFromMatchingElements(".ttsAccessibilityElement")(
      "ttsAccessibilityElement",
    ),
    removeClassFromMatchingElements(".srAccessibilityElement")(
      "srAccessibilityElement",
    ),
    removeClassFromMatchingElements(".srInsertedText")("srInsertedText"),
  ])(element);
};

const getContentLocation = (contentElement) => {
  const id = contentElement.getAttribute("id");
  const type = id.split("-")[0];
  let contentLocation = `${type}.content`;
  const re = new RegExp(
    `^${type}-interaction-(?<index>\\d+)-foil-(?<i>\\d+)-content$`,
  );
  if (re.test(id)) {
    const { groups } = id.match(re);
    contentLocation = `${type}.interactions[${groups.index}].foils[${groups.i}].content`;
  }
  return contentLocation;
};

const getCorrectDynamicTagPosition =
  (content) =>
  (tags = []) =>
  (tag) => {
    // If there are no tags, the one passed in is the first
    if (tags.length === 0) return 0;

    // If the tag is not in the array, order the tags and return its correct position
    return _.pipe([
      _.uniqWith((arrVal, othVal) => arrVal.id === othVal.id),
      self.orderDynamicTags(content),
      _.findIndex(_.matches(tag)),
    ])([tag, ...tags]);
  };

const getCorrectFoilContentFromItem = (item) => {
  if (!item.interactions?.length || item.interactions.length > 1) return "";

  const interaction = item.interactions[0];
  if (interaction.__typename !== "ChoiceInteraction") return "";

  const foils = interaction.foils?.filter(({ correct }) => correct);
  if (!foils?.length || foils.length > 1) return "";

  const element = document.createElement("div");
  element.innerHTML = foils[0].content;

  return Array.from(element.querySelectorAll("div,p"))
    .map((el) => el.innerHTML)
    .join("");
};

const getCurrentDynamicTagPosition =
  (tags = []) =>
  (tag) => {
    // // If the tag is already in the array, return its current position (includes an id because of RHF)
    const position = _.findIndex(_.matches({ id: tag.id }))(tags);
    if (position > -1) return position;
  };

const getDocumentFragmentContents = (fragment) => {
  if (!fragment) return "";

  const div = self.createElement({ element: "div", fragment });
  return getCleanContent(div);
};

const getDynamicContentElement = (passage) => (tag) => {
  const { content } = passage;
  return _.pipe([
    self.createElement,
    self.createParagraphUUIDs,
    self.createLabelElements,
    self.createHighlightElements(passage),
    self.createDynamicContentElements(passage)(tag),
  ])({
    content,
  });
};

const getDynamicModifyContentTagRange = (contentElement) => (tag) => {
  if (tag.range) return tag.range;

  const range = new Range();
  if (contentElement) {
    range.setStartAfter(
      contentElement.querySelector(`mark[data-uuid="${tag.begin}"]`),
    );
    range.setEndBefore(
      contentElement.querySelector(`mark[data-uuid="${tag.end}"]`),
    );
  }
  return range.cloneRange();
};

const getDynamicModifyContentTagReplacementText = (item) => (selectedText) => {
  const content = getCorrectFoilContentFromItem(item);
  const element = self.createElement({ content });
  if (/^no change$/i.test(element.textContent)) return selectedText;
  if (
    /^delete the (?:highlighted text|underlined portion)/i.test(
      element.textContent,
    )
  )
    return "";
  return content;
};

const getDynamicTagOptions =
  (content) =>
  (range) =>
  ({ highlights, itemID, items, labels }) => {
    if (
      range.startContainer.nodeType !== Node.TEXT_NODE ||
      range.endContainer.nodeType !== Node.TEXT_NODE
    ) {
      console.error("Dynamic tags cannot be applied to non-text nodes");
      return [];
    }
    if (
      !(
        range.commonAncestorContainer.compareDocumentPosition(content) &
        Node.DOCUMENT_POSITION_CONTAINS
      )
    ) {
      console.error("Dynamic tags cannot span multiple paragraphs");
      return [];
    }
    return [
      ...highlights.map((highlight, index) => {
        let highlightRange,
          replaceWith = "",
          underline;
        if (highlight?.__typename === "Highlight") {
          highlightRange = new Range();
          highlightRange.setStartAfter(
            content.querySelector(`mark[data-uuid="${highlight.begin}"]`),
          );
          highlightRange.setEndBefore(
            content.querySelector(`mark[data-uuid="${highlight.end}"]`),
          );
          underline =
            self
              .createElement({
                element: "div",
                fragment: highlightRange.cloneContents(),
              })
              .querySelector("span.passage-underline")?.textContent ===
            highlightRange.toString();
          if (highlight.items.length === 1) {
            const item = items.find(({ id }) => id === highlight.items.at(0));
            const selectedText = self.getSelectedTextFromRange(highlightRange);
            const element = self.createElement({
              element: "p",
              content:
                getDynamicModifyContentTagReplacementText(item)(selectedText),
            });
            replaceWith = element.outerHTML;
          }
        }
        return {
          __typename: "DynamicModifyContentTag",
          begin: highlight.begin,
          end: highlight.end,
          filter:
            highlight?.__typename === "Highlight" &&
            range.collapsed &&
            self.rangeInHighlight(content)(range)(highlight),
          highlight: true,
          items: highlight.items,
          label: `Modify Text (Highlight ${index + 1})`,
          range: highlightRange,
          replaceWith,
          underline,
        };
      }),
      {
        __typename: "DynamicModifyContentTag",
        begin: null,
        end: null,
        filter:
          range.collapsed &&
          self.rangeInNode(content)(range)("p") &&
          !self.rangeInPattern(range)(/\[[0-9A-Z]+\]/dgm),
        highlight: false,
        items: itemID ? [itemID] : [],
        label: "Insert Correct Text",
        range,
        replaceWith: "",
        underline: false,
      },
      {
        __typename: "DynamicOrderContentTag",
        filter:
          range.collapsed &&
          self.rangeInNode(content)(range)("p") &&
          !self.rangeInPattern(range)(/\[[0-9A-Z]+\]/dgm),
        items: [],
        label: "Reorder Paragraphs",
        order: getOrderedElements(content)(`p[data-uuid]`).map((el) =>
          el.getAttribute("data-uuid"),
        ),
      },
      {
        __typename: "DynamicRemoveLabelsTag",
        filter:
          range.collapsed && self.rangeInPattern(range)(/\[[0-9A-Z]+\]/dgm),
        items: [],
        label: "Remove Paragraph/Sentence Numbers",
        range,
        labels: labels.map(({ begin, end }) => ({ begin, end })),
      },
      {
        __typename: "DynamicModifyContentTag",
        begin: null,
        end: null,
        filter: !range.collapsed && self.rangeInNode(content)(range)("p"),
        highlight: false,
        items: [],
        label: "Modify/Remove Text",
        range,
        replaceWith: "",
        underline: true,
      },
      {
        __typename: "DynamicOrderContentTag",
        filter:
          range.collapsed &&
          self.rangeInNode(content)(range)("p") &&
          !self.rangeInPattern(range)(/\[[0-9A-Z]+\]/dgm) &&
          /\[[0-9A-Z]+\]/dgm.test(
            self.getSurroundingElements(content)(range)("p")[0].textContent,
          ),
        items: [],
        label: "Reorder Sentences",
        order: labels
          .filter(isSentenceNumberLabel(content))
          .map(({ begin }) => begin),
        range,
      },
      {
        __typename: "DynamicBreakParagraphTag",
        filter:
          range.collapsed &&
          !/^\s*\[[0-9A-Z]+\]\s*$/dgm.test(
            self.getSurroundingElements(content)(range)("p")[0].textContent,
          ) &&
          (self.rangeInPattern(range)(/\[[0-9A-Z]+\]/dgm) ||
            highlights.find(
              (o) =>
                o.__typename === "ItemPointReference" &&
                self.getSurroundingElements(content)(range)(
                  `mark[data-uuid="${o.point}"]`,
                ).length,
            )),
        items:
          highlights.find(
            (o) =>
              o.__typename === "ItemPointReference" &&
              self.getSurroundingElements(content)(range)(
                `mark[data-uuid="${o.point}"]`,
              ).length,
          )?.items || [],
        label: "Break Paragraph",
        point: self.getSurroundingElements(content)(range)("mark").length
          ? self
              .getSurroundingElements(content)(range)("mark")[0]
              .getAttribute("data-uuid")
          : labels.find((label) => {
              if (label.type !== "sentence") return false;

              const labelRange = new Range();
              const beginMark = content.querySelector(
                `mark[data-uuid="${label.begin}"]`,
              );
              const endMark = content.querySelector(
                `mark[data-uuid="${label.end}"]`,
              );
              labelRange.setStartBefore(beginMark);
              labelRange.setEndAfter(endMark);
              return labelRange.isPointInRange(
                range.startContainer,
                range.startOffset,
              );
            })?.begin,
        range,
      },
    ]
      .filter(({ filter }) => Boolean(filter))
      .map(({ _filter, ...option }) => option);
  };

const getHasUnresolvedChangeTags = ({
  choiceInteractions = [],
  contentSuggestions,
  referenceContentSuggestions,
}) =>
  (contentSuggestions && !!JSON.parse(contentSuggestions)?.length) ||
  (referenceContentSuggestions &&
    !!JSON.parse(referenceContentSuggestions)?.length) ||
  choiceInteractions?.some(({ foils }) =>
    foils.some(
      (foil) =>
        foil.contentSuggestions && JSON.parse(foil.contentSuggestions)?.length,
    ),
  );

const getItemNamesForTags = (items) => (tags) =>
  Array.from(
    new Set(
      tags
        .flatMap((tag) => {
          return tag.items
            .map((id) => items.find((item) => item.id === id)?.name)
            .filter(Boolean);
        })
        .sort((a, b) => a.localeCompare(b)),
    ),
  ).join(", ");

const TTS_BLOCK_SELECTOR =
  "p, div, li, td, th, blockquote, body, article, section";

const hasPrecedingProcessedTTSTag = (node) => {
  const block =
    node.parentElement?.closest(TTS_BLOCK_SELECTOR) ?? node.parentElement;
  if (!block) return false;
  return Array.from(block.querySelectorAll("span[id]")).some((span) => {
    const pos = node.compareDocumentPosition(span);
    return (pos & Node.DOCUMENT_POSITION_PRECEDING) !== 0;
  });
};

const getNextTTSRange =
  (options = {}) =>
  (contentElement) => {
    const filter = (node) => {
      return !self.isTextNodeInElement("span[id]")(node) &&
        /\S/.test(node.nodeValue) &&
        !/^[\u200B\u200C\u200D\uFEFF]+$/.test(node.nodeValue.trim()) &&
        !node.parentNode.closest(
          ".qti-visually-hidden,table:not(.perspectiveTable)",
        )
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    };

    const treeWalker = document.createTreeWalker(
      contentElement,
      NodeFilter.SHOW_TEXT,
      filter,
    );

    let quotedText;
    const range = new Range();
    while (treeWalker.nextNode()) {
      const node = treeWalker.currentNode;
      const mathMLNode = node.parentElement.closest("math");

      // If the start of the range has not been defined
      if (range.startContainer.nodeName === "#document") {
        // For English content, make standalone [*] or IPR mark * its own TTS range
        const isStandaloneBracketed =
          options.isEnglish && node.nodeValue.trim() === "[*]";
        const isStandaloneIPRMark =
          options.isEnglish &&
          node.nodeValue.trim() === "*" &&
          node.parentElement.matches("mark[data-uuid]");
        if (
          (isStandaloneBracketed || isStandaloneIPRMark) &&
          hasPrecedingProcessedTTSTag(node)
        ) {
          range.setStart(node, 0);
          range.setEnd(node, node.nodeValue.length);
          break;
        }
        if (mathMLNode) {
          const startTextNode = document.createTextNode("");
          mathMLNode.before(startTextNode);
          range.setStart(startTextNode, 0);
        } else {
          range.setStart(node, 0);
        }
        quotedText = /^\s*(?:\u0022|&#34;|\u201C|&#8220;)/.test(node.nodeValue);
      }

      // See if the end of the current range should fall within the current node
      const re = quotedText
        ? /[!?.](?:\u0022|&#34;|\u201D|&#8221;)(?:\s|$)/
        : /[!?.](?:\s|$)/;
      const match = node.nodeValue.match(re);

      // If we don't find an appropriate ending pattern, move along
      if (!match) continue;

      // If the pattern was found inside mathML, move along
      if (mathMLNode) continue;

      const offset = match.index + (quotedText ? 2 : 1);
      range.setEnd(node, offset);
      break;
    }
    if (range.startContainer.nodeName !== "#document" && range.collapsed) {
      const lastNode =
        range.startContainer.parentNode.childNodes[
          range.startContainer.parentNode.childNodes.length - 1
        ];
      if (lastNode.nodeType === Node.TEXT_NODE) {
        range.setEnd(lastNode, lastNode.nodeValue.length);
      } else {
        const endTextNode = document.createTextNode("");
        lastNode.after(endTextNode);
        range.setEnd(endTextNode, 0);
      }
    }
    return range;
  };

const getOrderedElements = (content) => (selector) => {
  const element =
    typeof content === "string"
      ? self.createElement({ content, element: "div" })
      : content;

  // Handle Writing Prompt template element order (CDE-13439)
  const perspectiveTables = [
    ...element.querySelectorAll("table#perspectiveTable"),
  ];
  if (perspectiveTables.length) {
    perspectiveTables.map((table) => {
      const perspectives = [
        ...table.querySelectorAll("tr#perspectiveTableBodyRow td.perspective"),
      ];
      [
        ...table.querySelectorAll(
          "tr#perspectiveTableHeadRow th.perspectiveTitle",
        ),
      ].map((th, i) => {
        th.after(perspectives[i]);
      });
    });
  }
  return Array.from(element.querySelectorAll(selector));
};

const getElements = (content) => (selector) => {
  const element =
    typeof content === "string"
      ? self.createElement({ content, element: "div" })
      : content;
  return Array.from(element.querySelectorAll(selector));
};

const getScreenReaderTags =
  (type) =>
  ({ content = "", ...obj }) => {
    const element = self.createElement({});
    const contentElement = self.createElement({
      attributes: { className: `accessibility-content`, id: `${type}-content` },
      content,
      element: "div",
    });
    element.append(contentElement);

    wrapImgTags(contentElement);

    if (type === "item") {
      [
        ...element.querySelectorAll(`section[data-interaction-type="Choice"]`),
      ].map((el) => {
        const index = obj.interactions.findIndex(
          ({ identifier }) =>
            identifier === el.getAttribute("data-interaction-name"),
        );
        obj.interactions[index]?.foils?.map((foil, i) => {
          const foilElement = self.createElement({
            attributes: {
              className: `accessibility-content`,
              id: `${type}-interaction-${index}-foil-${i}-content`,
            },
            content: foil.content,
            element: "div",
          });
          element.append(foilElement);
          wrapImgTags(foilElement);
        });
      });
    }
    const tags = [...element.querySelectorAll(".qti-visually-hidden")]
      .map((el) => {
        const hiddenIdentifier = el.getAttribute("id");

        let tagName;
        [
          ...element.querySelectorAll(
            `[aria-describedby="${hiddenIdentifier}"]`,
          ),
        ].map((n) => {
          tagName = n.tagName === "IMG";
          if (n.tagName === "math" || tagName || n.textContent) {
            el.classList.add("srNotInsertedText");
          }
          // pass target id back to front end to find image alt textß
          return n.classList.add("srAccessibilityElement");
        });
        return {
          tagName,
          identifier: hiddenIdentifier,
          content: el.innerHTML,
        };
      })
      .filter(Boolean);

    const orderedElements = getOrderedElements(element.innerHTML)(
      ".srAccessibilityElement",
    );

    return [
      element.innerHTML,
      tags.sort(
        (a, b) =>
          orderedElements.findIndex(
            (el) => el.getAttribute("id") === a.identifier,
          ) -
          orderedElements.findIndex(
            (el) => el.getAttribute("id") === b.identifier,
          ),
      ),
    ];
  };

const getSelectedText = (fragment) => {
  return _.pipe([
    getDocumentFragmentContents,
    (content) => createElement({ content }),
    unwrapElements("span.passage-underline"),
    (element) => element.innerHTML,
  ])(fragment);
};

const getSelectedTextFromRange = (range) =>
  range?.toString() === "[i]"
    ? ""
    : self.getSelectedText(range?.cloneContents());

const getSentenceRanges = (content) => (labels) => {
  const sentenceLabels = labels.filter(isSentenceNumberLabel(content));
  let el;
  return sentenceLabels
    .map(({ begin: id, end: uuid }, index) => {
      // Find the surrounding paragraph
      if (!el)
        el = content.querySelector(`mark[data-uuid="${uuid}"]`).closest("p");

      const range = new Range();
      const mark = el.querySelector(`mark[data-uuid="${uuid}"]`);
      if (!mark) return;

      // Default to selecting all of the paragraph contents to handle the last label
      range.selectNodeContents(el);
      range.setStartAfter(mark);

      // NOTE: This assumes that the next tag is in the same paragraph
      const next =
        sentenceLabels[index + 1]?.begin &&
        el.querySelector(
          `mark[data-uuid="${sentenceLabels[index + 1]?.begin}"]`,
        );
      if (next) range.setEndBefore(next);

      return { id, range };
    })
    .filter(Boolean);
};

const getSROptions = (content) => (range) => {
  if (
    range.startContainer.parentElement.closest(".srAccessibilityElement") ||
    range.endContainer.parentElement.closest(".srAccessibilityElement") ||
    range.cloneContents().querySelector(".srAccessibilityElement")
  ) {
    console.error("Screen reader tags may not be nested within each other");
    return [];
  }

  if (
    !(
      range.commonAncestorContainer.compareDocumentPosition(content) &
      Node.DOCUMENT_POSITION_CONTAINS
    )
  ) {
    console.error("Selection must be wholly contained within the content");
    return [];
  }
  if (
    !range.collapsed &&
    range.startContainer.nodeType === Node.TEXT_NODE &&
    range.endContainer.nodeType === Node.TEXT_NODE &&
    range.startContainer.parentElement.closest("p") !==
      range.endContainer.parentElement.closest("p")
  ) {
    console.error("Selection must not span paragraphs");
    return [];
  }

  return [
    {
      elements: [range.startContainer.parentElement.closest("math")],
      filter:
        range.collapsed &&
        range.startContainer.nodeType === Node.TEXT_NODE &&
        self.rangeInNode(content)(range)("math"),
      label: "MathML",
    },
    {
      elements: [range.startContainer],
      filter:
        range.collapsed &&
        range.startContainer.className === "accessibility-image",
      label: "Image",
    },
    {
      elements: [range.startContainer.nextSibling],
      filter:
        range.collapsed &&
        range.startContainer?.nextSibling?.className === "accessibility-image",
      label: "Image",
    },
    {
      filter:
        range.collapsed &&
        range.startContainer.nodeType === Node.TEXT_NODE &&
        !self.rangeInNode(content)(range)("math"),
      label: "Insert Text",
      range,
    },
    {
      filter:
        !range.collapsed &&
        range.startContainer.nodeType === Node.TEXT_NODE &&
        range.endContainer.nodeType === Node.TEXT_NODE,
      label: "Selected Text",
      range,
    },
  ]
    .filter(({ filter }) => Boolean(filter))
    .map(({ _filter, ...option }) => ({
      ...option,
      tag: {
        identifier: null,
      },
    }));
};

const getSurroundingElements = (content) => (range) => (selector) => {
  return self
    .getOrderedElements(content)(selector)
    .filter(
      (el) =>
        el.contains(range.startContainer) && el.contains(range.endContainer),
    );
};

const wrapImgTags = (contentElement) => {
  const images = contentElement.querySelectorAll("img");
  images.forEach((image) => {
    const parent = image.parentNode;
    const wrapper = self.createElement({
      attributes: {
        className: "accessibility-image",
        id: `${image.getAttribute("src")}`,
      },
      element: "span",
    });
    parent.replaceChild(wrapper, image);
    wrapper.appendChild(image);
  });
};
const getTextToSpeechTags =
  (type) =>
  ({ accessibilityElements = [], content = "", ...obj }) => {
    const element = self.createElement({});
    const contentElement = self.createElement({
      attributes: {
        className: `accessibility-content`,
        id: `${type}-content`,
      },
      content,
      element: "div",
    });
    element.append(contentElement);

    wrapImgTags(contentElement);

    if (type === "item") {
      [
        ...element.querySelectorAll(`section[data-interaction-type="Choice"]`),
      ].map((el) => {
        const index = obj.interactions.findIndex(
          (interaction) =>
            interaction.identifier === el.getAttribute("data-interaction-name"),
        );
        obj.interactions[index]?.foils?.map((foil, i) => {
          const foilElement = self.createElement({
            attributes: {
              className: `accessibility-content`,
              id: `${type}-interaction-${index}-foil-${i}-content`,
            },
            content: foil.content,
            element: "div",
          });
          element.append(foilElement);
          wrapImgTags(foilElement);
        });
      });
    }
    if (!accessibilityElements.length) return [element.innerHTML, []];

    accessibilityElements.map(({ identifier }) => {
      // aria-described by to get img tags
      [
        ...element.querySelectorAll(
          `[id="${identifier}"], [aria-describedby="${identifier}"]`,
        ),
      ].map((el) => el.classList.add("ttsAccessibilityElement"));
    });

    const orderedElements = getOrderedElements(element.innerHTML)(
      ".ttsAccessibilityElement",
    );

    return [
      element.innerHTML,
      accessibilityElements
        .filter(({ identifier }) =>
          element.querySelector(
            `[id="${identifier}"], [aria-describedby="${identifier}"]`,
          ),
        )
        .sort(
          (a, b) =>
            orderedElements.findIndex(
              (el) => el.getAttribute("id") === a.identifier,
            ) -
            orderedElements.findIndex(
              (el) => el.getAttribute("id") === b.identifier,
            ),
        ),
    ];
  };

const updateFoilTTSPrefixes = (
  accessibilityElements = [],
  interaction = {},
  reorderedFoils = [],
) => {
  if (
    !accessibilityElements.length ||
    !reorderedFoils.length ||
    !interaction.foils
  )
    return { elements: accessibilityElements, regenerate: false };

  let changed = false;
  const letterPrefixPattern = /^[A-Z]+;\s*/;

  const elements = accessibilityElements.map((tag) => {
    const foilIndex = reorderedFoils.findIndex((foil) => {
      const div = self.createElement({});
      div.innerHTML = foil.content;
      return div.querySelector(`span[id="${tag.identifier}"]`);
    });

    if (foilIndex === -1) return tag;

    const oldText = tag?.accessibilityFeatures?.[0]?.text || "";
    if (!letterPrefixPattern.test(oldText)) return tag;
    const newText = `${getLetter(foilIndex)}; ${oldText.replace(letterPrefixPattern, "")}`;
    if (oldText === newText) return tag;
    changed = true;
    return {
      ...tag,
      accessibilityFeatures: [
        { ...tag.accessibilityFeatures[0], text: newText },
      ],
    };
  });
  return { elements, regenerate: changed };
};

const getTrimmedRange = (initialRange) => {
  const range = initialRange.cloneRange();
  if (range.collapsed) return range;

  const whitespaceCharacters = [
    "\u0009",
    "\u000A",
    "\u000B",
    "\u000C",
    "\u000D",
    "\u0020",
    //        "\u0085",
    "\u00A0",
    //        "\u1680",
    "\u180E",
    "\u2000",
    "\u2001",
    "\u2002",
    "\u2003",
    "\u2004",
    "\u2005",
    "\u2006",
    "\u2007",
    "\u2008",
    "\u2009",
    "\u200A",
    "\u200B",
    "\u200C",
    "\u200D",
    "\u2028",
    "\u2029",
    "\u202F",
    "\u205F",
    "\u2060",
    "\u3000",
    "\uFEFF",
  ].join("");

  // While the selection has whitespace at the beginning
  while (
    range.toString() !==
    _.trimCharsStart(whitespaceCharacters)(range.toString())
  ) {
    // Adjust the range to remove the whitespace
    if (
      range.startContainer.nodeType === Node.TEXT_NODE &&
      range.startOffset < range.startContainer.nodeValue.length
    ) {
      range.setStart(range.startContainer, range.startOffset + 1);
    } else {
      range.setStartAfter(range.startContainer);
    }
  }

  // While the selection has whitespace at the end
  while (
    range.toString() !== _.trimCharsEnd(whitespaceCharacters)(range.toString())
  ) {
    // Adjust the range to remove the whitespace
    if (
      range.endContainer.nodeType === Node.TEXT_NODE &&
      range.endOffset === 0
    ) {
      let prevNode = range.endContainer.previousSibling;
      while (prevNode && prevNode.nodeType !== Node.TEXT_NODE) {
        prevNode = prevNode.previousSibling;
      }
      if (prevNode) {
        range.setEnd(prevNode, prevNode.nodeValue.length);
      } else {
        range.setEndBefore(range.endContainer);
        if (range.collapsed) break;
      }
    } else if (range.endOffset) {
      range.setEnd(range.endContainer, range.endOffset - 1);
    } else {
      range.setEndBefore(range.endContainer);
    }
  }
  return range;
};

const getTTSOptions = (content) => (range) => {
  if (
    !(
      range.commonAncestorContainer.compareDocumentPosition(content) &
      Node.DOCUMENT_POSITION_CONTAINS
    )
  ) {
    console.error("Selection must be wholly contained within the content");
    return [];
  }
  if (
    !range.collapsed &&
    range.startContainer.nodeType === Node.TEXT_NODE &&
    range.endContainer.nodeType === Node.TEXT_NODE &&
    range.startContainer.parentElement.closest("p") !==
      range.endContainer.parentElement.closest("p")
  ) {
    console.error("Selection must not span paragraphs");
    return [];
  }

  return [
    {
      elements: [range.startContainer.parentElement.closest("p")],
      filter:
        range.collapsed &&
        range.startContainer.nodeType === Node.TEXT_NODE &&
        self.rangeInNode(content)(range)("p"),
      label: "Paragraph",
    },
    {
      elements: [range.startContainer.parentElement.closest("math")],
      filter:
        range.collapsed &&
        range.startContainer.nodeType === Node.TEXT_NODE &&
        self.rangeInNode(content)(range)("math"),
      label: "MathML",
    },
    {
      elements: [range.startContainer.firstChild],
      filter:
        range.startContainer?.firstChild?.className === "accessibility-image",
      label: "Image",
    },
    {
      elements: [range.startContainer],
      filter:
        range.collapsed &&
        range.startContainer.className === "accessibility-image",
      label: "Image",
    },
    {
      elements: [range.startContainer.nextSibling],
      filter:
        range.collapsed &&
        range.startContainer?.nextSibling?.className === "accessibility-image",
      label: "Image",
    },
    {
      elements: [range.startContainer.parentElement.closest("td,th")],
      filter: range.collapsed && self.rangeInNode(content)(range)("td,th"),
      label: "Cell",
      range,
    },
    {
      elements: [range.startContainer.parentElement.closest("tr")],
      filter: range.collapsed && self.rangeInNode(content)(range)("tr"),
      label: "Row",
      range,
    },
    {
      elements: [range.startContainer.parentElement.closest("table")],
      filter: range.collapsed && self.rangeInNode(content)(range)("table"),
      label: "Table",
      range,
    },
    {
      filter:
        !range.collapsed &&
        range.startContainer.nodeType === Node.TEXT_NODE &&
        range.endContainer.nodeType === Node.TEXT_NODE,
      label: "Specific Text",
      range,
    },
  ]
    .filter(({ filter }) => Boolean(filter))
    .map(({ _filter, ...option }) => {
      let specificText =
        option.label === "Specific Text"
          ? getVisibleTextFromRange(range)
          : undefined;
      return {
        ...option,
        tag: {
          __typename: "AccessibilityElement",
          accessibilityFeatures: [
            {
              __typename: "AccessibilityFeature",
              feature: "Text to Speech",
              language: "",
              src: [],
              text: option.elements
                ? getTextContent(option.elements)
                : specificText || range.toString(),

              type: "Spoken",
            },
          ],
          identifier: null,
          inclusionOrders: [
            "Spoken, Text and Graphics : Default",
            "Spoken, Text and Graphics : On Demand",
          ],
          label: "Selected Text",
        },
      };
    });
};

const getVisibleTextFromRange = (range) => {
  const element = self.createElement({
    fragment: range.cloneContents(),
  });
  [...element.querySelectorAll(".qti-visually-hidden")].forEach((el) =>
    el.remove(),
  );
  return element.textContent;
};

const getTextContent = (elements) => {
  return elements
    .map((el) => {
      const clonedEl = el.cloneNode(true);
      clonedEl
        .querySelectorAll(".qti-visually-hidden")
        .forEach((qtiEl) => qtiEl.remove());
      return clonedEl.textContent;
    })
    .join("");
};

const insertDynamicTagIntoContent = (element) => (tag) => {
  switch (tag.__typename) {
    case "DynamicModifyContentTag":
      return self.insertModifyContentTag(element)(tag);
    case "DynamicBreakParagraphTag":
    case "DynamicOrderContentTag":
    case "DynamicRemoveLabelsTag":
      return [element, tag];
  }
};

const insertModifyContentTag =
  (element) =>
  ({ range, ...tag }) => {
    const beginMark = tag.begin
      ? element.querySelector(`mark[data-uuid="${tag.begin}"]`)
      : self.createElement({
          attributes: { "data-uuid": uuidv4() },
          element: "mark",
        });
    const endMark = tag.end
      ? element.querySelector(`mark[data-uuid="${tag.end}"]`)
      : self.createElement({
          attributes: { "data-uuid": uuidv4() },
          element: "mark",
        });
    const parentEl = range.startContainer.parentElement;
    // If the user selected an IPR
    if (
      range.collapsed &&
      parentEl.tagName === "MARK" &&
      parentEl.textContent === "*"
    ) {
      range.setStartBefore(parentEl);
      range.setEndAfter(parentEl);
    }
    if (tag.underline) {
      const underlineElement = Array.from(
        element.querySelectorAll("span.passage-underline"),
      ).find((el) => {
        return (
          el.textContent === range.toString() &&
          el.compareDocumentPosition(beginMark) ===
            Node.DOCUMENT_POSITION_PRECEDING &&
          el.compareDocumentPosition(endMark) ===
            Node.DOCUMENT_POSITION_FOLLOWING
        );
      });
      if (underlineElement) {
        const childNodes = Array.from(underlineElement.childNodes);
        underlineElement.replaceWith(...childNodes);
      }
    }
    if (
      element.compareDocumentPosition(beginMark) &
      Node.DOCUMENT_POSITION_DISCONNECTED
    ) {
      range.insertNode(beginMark);
    }
    if (
      element.compareDocumentPosition(endMark) &
      Node.DOCUMENT_POSITION_DISCONNECTED
    ) {
      range.collapse();
      range.insertNode(endMark);
    }

    return [
      element,
      {
        ...tag,
        begin: beginMark.getAttribute("data-uuid"),
        end: endMark.getAttribute("data-uuid"),
      },
    ];
  };

const isBlockElement = (n) =>
  [
    "ADDRESS",
    "ARTICLE",
    "ASIDE",
    "BLOCKQUOTE",
    "DETAILS",
    "DIALOG",
    "DD",
    "DIV",
    "DL",
    "DT",
    "FIELDSET",
    "FIGCAPTION",
    "FIGURE",
    "FOOTER",
    "FORM",
    "Hd",
    "HEADER",
    "HGROUP",
    "HR",
    "LI",
    "MAIN",
    "NAV",
    "OL",
    "P",
    "PRE",
    "SECTION",
    "TABLE",
    "TD",
    "TH",
    "TR",
    "UL",
  ].includes(n.nodeName.toUpperCase());

const isBreakParagraphMarkElement =
  (tags = []) =>
  (n) =>
    n.nodeName.toUpperCase() === "MARK" &&
    n.hasAttribute("data-uuid") &&
    tags.some(
      (tag) =>
        tag.__typename === "DynamicBreakParagraphTag" &&
        tag.point === n.getAttribute("data-uuid"),
    );

const isElement = (tagName) => (n) =>
  n.nodeName.toUpperCase() === tagName.toUpperCase();

const isHighlightMarkElement =
  (highlights = []) =>
  (n) =>
    n.nodeName.toUpperCase() === "MARK" &&
    n.hasAttribute("data-uuid") &&
    highlights.some(
      (highlight) =>
        highlight.__typename === "Highlight" &&
        [highlight.begin, highlight.end].includes(n.getAttribute("data-uuid")),
    );

const isItemPointReferenceMarkElement =
  (highlights = []) =>
  (n) =>
    n.nodeName.toUpperCase() === "MARK" &&
    n.hasAttribute("data-uuid") &&
    highlights.some(
      (highlight) =>
        highlight.__typename === "ItemPointReference" &&
        highlight.point === n.getAttribute("data-uuid"),
    );

const isLabelMarkElement =
  (labels = []) =>
  (n) =>
    n.nodeName.toUpperCase() === "MARK" &&
    n.hasAttribute("data-uuid") &&
    labels.some((label) =>
      [label.begin, label.end].includes(n.getAttribute("data-uuid")),
    );

const isLetterPointLabel =
  (content) =>
  ({ begin, end }) => {
    const el = content.querySelector(`mark[data-uuid="${begin}"]`).closest("p");

    const range = new Range();
    range.setStartAfter(el.querySelector(`mark[data-uuid="${begin}"]`));
    range.setEndBefore(el.querySelector(`mark[data-uuid="${end}"]`));

    return (
      /\[[A-Z]+\]/.test(range.toString()) &&
      !/^\s*\[[A-Z]+\]\s*$/.test(el.textContent)
    );
  };

const isModifyContentMarkElement =
  (tags = []) =>
  (n) =>
    n.nodeName.toUpperCase() === "MARK" &&
    n.hasAttribute("data-uuid") &&
    tags.some(
      (tag) =>
        tag.__typename === "DynamicModifyContentTag" &&
        [tag.begin, tag.end].includes(n.getAttribute("data-uuid")),
    );

const isOrderContentMarkElement =
  (tags = []) =>
  (n) =>
    n.nodeName.toUpperCase() === "MARK" &&
    n.hasAttribute("data-uuid") &&
    tags.some(
      (tag) =>
        tag.__typename === "DynamicOrderContentTag" &&
        tag.order.includes(n.getAttribute("data-uuid")),
    );

const isParagraphNumberLabel =
  (content) =>
  ({ begin, end }) => {
    const el = content.querySelector(`mark[data-uuid="${begin}"]`).closest("p");

    const range = new Range();
    range.setStartAfter(el.querySelector(`mark[data-uuid="${begin}"]`));
    range.setEndBefore(el.querySelector(`mark[data-uuid="${end}"]`));

    return /^\s*\[[0-9A-Z]+\]\s*$/.test(el.textContent);
  };

const isRemoveLabelsMarkElement =
  (tags = []) =>
  (n) =>
    n.nodeName.toUpperCase() === "MARK" &&
    n.hasAttribute("data-uuid") &&
    tags.some(
      (tag) =>
        tag.__typename === "DynamicRemoveLabelsTag" &&
        tag.labels.some((label) =>
          [label.begin, label.end].includes(n.getAttribute("data-uuid")),
        ),
    );

const isSentenceNumberLabel =
  (content) =>
  ({ begin, end }) => {
    const el = content.querySelector(`mark[data-uuid="${begin}"]`).closest("p");

    const range = new Range();
    range.setStartAfter(el.querySelector(`mark[data-uuid="${begin}"]`));
    range.setEndBefore(el.querySelector(`mark[data-uuid="${end}"]`));

    return (
      /\[\d+\]/.test(range.toString()) &&
      !/^\s*\[\d+\]\s*$/.test(el.textContent)
    );
  };

const isSugggestionElement = (n) => {
  ["SUGGESTION-START", "SUGGESTION-END"].includes(n.nodeName.toUpperCase()) ||
    (Object.hasOwn(n, "attributes") &&
      [...n.attributes].some((attr) =>
        /^data-suggestion-(?:start|end)-/.test(attr.name),
      ));
};

const isTextNodeInElement = (selector) => (n) => {
  if (n.nodeType !== Node.TEXT_NODE || !n.parentNode) return null;
  return n.parentNode.closest(selector);
};

const isVisible = (n) => {
  if (n.nodeType === Node.TEXT_NODE) {
    return n.parentElement.closest(".qti-visually-hidden")
      ? false
      : n.nodeValue.length;
  }
  return n.offsetHeight || n.offsetWidth;
};

const orderDynamicTags = (content) => (tags) => {
  if (tags.length === 0) return [];

  const tagUUIDs = self
    .getOrderedElements(content)(`mark`)
    .map((el) => el.getAttribute("data-uuid"));
  return _.sortBy([
    ({ __typename, ...t }) => {
      // eslint-disable-next-line default-case
      switch (__typename) {
        case "DynamicModifyContentTag":
        case "Highlight":
          return tagUUIDs.indexOf(t.begin);
        case "DynamicBreakParagraphTag":
        case "ItemPointReference":
          return tagUUIDs.indexOf(t.point);
        case "DynamicOrderContentTag":
          return tagUUIDs.indexOf(t.order[0]);
        case "DynamicRemoveLabelsTag":
          return tagUUIDs.indexOf(t.labels[0].begin);
      }
    },
    ({ __typename, ...t }) => {
      // eslint-disable-next-line default-case
      switch (__typename) {
        case "Highlight":
        case "DynamicModifyContentTag":
          return tagUUIDs.indexOf(t.end);
        case "DynamicBreakParagraphTag":
        case "ItemPointReference":
          return tagUUIDs.indexOf(t.point);
        case "DynamicOrderContentTag":
          return tagUUIDs.indexOf(t.order[0]);
        case "DynamicRemoveLabelsTag":
          return tagUUIDs.indexOf(t.labels[0].begin);
      }
    },
  ])([...tags]);
};

const parentsUntil =
  (ancestorNode, nodes = []) =>
  (n) => {
    if (n.nodeType === Node.TEXT_NODE)
      return parentsUntil(ancestorNode, [...nodes])(n.parentElement);

    let match;
    switch (typeof ancestorNode) {
      case "object":
        match = ancestorNode instanceof Node && n === ancestorNode;
        break;
      case "string":
        match = n.matches(ancestorNode);
        break;
      default:
        match = false;
    }
    return match
      ? nodes
      : parentsUntil(ancestorNode, [...nodes, n])(n.parentNode);
  };

const rangeInHighlight = (content) => (range) => (highlight) => {
  const beginHL = content.querySelector(`mark[data-uuid="${highlight.begin}"]`);
  const endHL = content.querySelector(`mark[data-uuid="${highlight.end}"]`);
  return (
    !!(
      range.startContainer.compareDocumentPosition(beginHL) &
      Node.DOCUMENT_POSITION_PRECEDING
    ) &&
    !!(
      range.endContainer.compareDocumentPosition(endHL) &
      Node.DOCUMENT_POSITION_FOLLOWING
    )
  );
};

const rangeInNode = (content) => (range) => (selector) => {
  return getElements(content)(selector).some(
    (span) =>
      span.contains(range.startContainer) && span.contains(range.endContainer),
  );
};

const rangeInPattern = (range) => (re) => {
  if (!range.collapsed) return false;
  if (range.startContainer.nodeType !== Node.TEXT_NODE) return false;

  let result;
  while ((result = re.exec(range.startContainer.textContent))) {
    if (
      range.startOffset >= result.indices[0][0] &&
      range.endOffset < result.indices[0][1]
    )
      return true;
  }
  return false;
};

const removeAllMarkTags = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("mark[data-uuid]")].map((mark) => mark.remove());
  return element.innerHTML;
};

const removeClassMsoNormalAttr = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll(`[class^="Mso"]`)].map((elmnt) =>
    elmnt.removeAttribute("class"),
  );
  return element.innerHTML;
};

const removeFontFamilyFromStyle = (content) => {
  const element = createElement({ content });
  const elements = element.querySelectorAll('[style*="font-family"]');

  elements.forEach((elmnt) => {
    let styleAttr = elmnt.getAttribute("style");
    const properties = styleAttr.split(";");
    const filteredProperties = properties.filter(
      (property) => !property.includes("font-family"),
    );
    const newStyleValue = filteredProperties.join(";").trim();

    if (newStyleValue === "") {
      elmnt.removeAttribute("style");
    } else {
      elmnt.setAttribute("style", newStyleValue);
    }
  });

  return element.innerHTML;
};

const removeFromContent = (selector) => (content) => {
  const contentElement = createElement({ content });

  [...contentElement.querySelectorAll(selector)].map((el) => {
    el.remove();
  });

  return contentElement.innerHTML;
};

const removeFontWeight400FromStyle = (content) => {
  const element = createElement({ content });
  const elements = element.querySelectorAll('[style*="font-weight"]');

  elements.forEach((elmnt) => {
    const styleAttr = elmnt.getAttribute("style");
    if (!styleAttr) return;

    const properties = styleAttr.split(";");
    const filteredProperties = properties.filter((property) => {
      const trimmed = property.trim();
      return (
        !trimmed.includes("font-weight:400") &&
        !trimmed.includes("font-weight: 400")
      );
    });

    const newStyleValue = filteredProperties.join(";").trim();

    if (newStyleValue === "" || newStyleValue === ";") {
      elmnt.removeAttribute("style");
    } else {
      elmnt.setAttribute("style", newStyleValue);
    }
  });

  return element.innerHTML;
};

const removeInsertCorrectTextPlaceholders = (element) => {
  [...element.querySelectorAll("i")].map((el) => {
    if (el.textContent === "[i]") el.remove();
  });
  return element;
};

const removeMarkDataSuggestion = (suggestions) => (content) => {
  const element = createElement({ content });

  let contentSuggestionList = JSON.parse(suggestions) || [];

  contentSuggestionList = contentSuggestionList.map((cs) =>
    [cs.type, cs.id, cs.authorId].join(":"),
  );

  const suggestionElements = [
    ...element.querySelectorAll(
      "suggestion-start, [data-suggestion-start-before],[data-suggestion-start-after], suggestion-end, [data-suggestion-end-before],[data-suggestion-end-after]",
    ),
  ];

  suggestionElements.forEach((suggestionElement) => {
    [...suggestionElement.attributes]
      .filter((attr) => /^data-suggestion-(?:start|end)-/.test(attr.name))
      .forEach((attribute) => {
        const uniqueArr = [...new Set(attribute.value.split(","))].filter((e) =>
          contentSuggestionList.includes(e),
        );

        if (uniqueArr.length > 0) {
          const newValue = uniqueArr.join(",");
          suggestionElement.setAttribute(attribute.name, newValue);
        } else {
          suggestionElement.removeAttribute(attribute.name);
        }
      });

    if (
      suggestionElement.nodeName.toUpperCase() === "SUGGESTION-START" ||
      suggestionElement.nodeName.toUpperCase() === "SUGGESTION-END"
    ) {
      const attrArr = suggestionElement.getAttribute("name").split(",");
      const uniqueArr = [...new Set(attrArr)].filter((e) =>
        contentSuggestionList.includes(e),
      );
      if (uniqueArr.length > 0) {
        const newValue = uniqueArr.join(",");
        suggestionElement.setAttribute("name", newValue);
      } else {
        suggestionElement.remove();
      }
    }
  });

  return element.innerHTML;
};

const removeNBSPAdjacentToIPR = (name) => (formData) => (content) => {
  if (name !== "passage.content") {
    return content;
  }
  const highlights = _.get("passage.highlights")(formData) || [];
  if (highlights.length === 0) {
    return content;
  }
  const element = createElement({ content });
  highlights
    .filter(({ __typename }) => __typename === "ItemPointReference")
    .map((hl) => {
      const mark = element.querySelector(`mark[data-uuid="${hl.point}"]`);
      const previousRE = /(?:&nbsp;|\u00A0)$/;
      const previousTextNode = findLastMatchingTextNode(previousRE)(
        mark.previousSibling,
      );
      if (previousTextNode) {
        previousTextNode.textContent = previousTextNode.textContent.replace(
          previousRE,
          " ",
        );
      }
      const nextRE = /^(?:&nbsp;|\u00A0)/;
      const nextTextNode = findFirstMatchingTextNode(nextRE)(mark.nextSibling);
      if (nextTextNode) {
        nextTextNode.textContent = nextTextNode.textContent.replace(
          nextRE,
          " ",
        );
      }
    });
  return element.innerHTML;
};

const removeNBSPAdjacentToSpanMath = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("span[style='white-space:nowrap;']")].forEach(
    (el) => {
      if (
        el.hasChildNodes() &&
        Array.from(el.childNodes).find((node) => {
          return node.nodeName === "math";
        })
      ) {
        const previousRE = /(?:&nbsp;|\u00A0)$/;
        if (
          el.previousSibling?.nodeType === Node.TEXT_NODE &&
          previousRE.test(el.previousSibling.textContent)
        ) {
          el.previousSibling.textContent =
            el.previousSibling.textContent.replace(previousRE, " ");
        }

        const nextRE = /^(?:&nbsp;|\u00A0)/;
        if (
          el.nextSibling?.nodeType === Node.TEXT_NODE &&
          nextRE.test(el.nextSibling.textContent)
        ) {
          el.nextSibling.textContent = el.nextSibling.textContent.replace(
            nextRE,
            " ",
          );
        }
      }
    },
  );

  return element.innerHTML;
};

const removeStyleFromTableHeader = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("th")].forEach((tableTh) => {
    tableTh.style.removeProperty("text-align");
    tableTh.style.removeProperty("vertical-align");
    tableTh.style.removeProperty("font-weight");
  });

  return element.innerHTML;
};

const removeUnnecessaryMarkTags =
  ({ dynamicTags = [], highlights = [] }) =>
  (element) => {
    const uuids = [...dynamicTags, ...highlights].flatMap(
      ({ __typename, ...tag }) => {
        switch (__typename) {
          case "DynamicBreakParagraphTag":
          case "ItemPointReference":
            return [tag.point];
          case "DynamicModifyContentTag":
          case "Highlight":
            return [tag.begin, tag.end];
          case "DynamicOrderContentTag":
            return tag.order;
          case "DynamicRemoveLabelsTag":
            return tag.labels.flatMap((label) => [label.begin, label.end]);
        }
      },
    );
    Array.from(element.querySelectorAll("mark,p"))
      .filter((m) => !uuids.includes(m.getAttribute("data-uuid")))
      .map((m) => {
        if (m.tagName.toLowerCase() === "mark") m.remove();
        if (m.tagName.toLowerCase() === "p") m.removeAttribute("data-uuid");
      });
    return element;
  };

const removeXMLNSAttributesFromMathML = (content) => {
  const element = createElement({ content });
  [...element.querySelectorAll("math")].map((el) => {
    if (el.hasAttribute("xmlns")) el.removeAttribute("xmlns");
  });
  return element.innerHTML;
};

const replaceNBSPAdjacentToIPR = (name) => (formData) => (content) => {
  if (name !== "passage.content") {
    return content;
  }
  const highlights = _.get("passage.highlights")(formData) || [];

  function replaceItemPointReference(match, p1, p2, p3) {
    return `${p1 ? "&nbsp;" : ""}${p2}${p3 ? "&nbsp;" : ""}`;
  }

  highlights
    .filter(({ __typename }) => __typename === "ItemPointReference")
    .map((hl) => {
      const itemPointReferenceRE = new RegExp(
        `(\\s?)(<mark data-uuid="${hl.point}">\\*<\\/mark>)(\\s?)`,
      );

      content = content.replace(
        itemPointReferenceRE,
        replaceItemPointReference,
      );
    });

  return content;
};

const transformContentForEditor = (name) => (data) =>
  _.pipe([
    adjustEMText,
    replaceNBSPAdjacentToIPR(name)(data),
    removeNBSPAdjacentToSpanMath,
    removeStyleFromTableHeader,
    addXMLNSAttributesToMathML,
    // DOM navigation/manipulation above this line
    encodeMathMLTags,
    convertUTF8ToHTMLEntities,
  ]);

const repairOrphanSuggestionCounterpart =
  (element) =>
  ({ orphanTag, counterpartTag, siblingKey, insertMethod }) => {
    [...element.querySelectorAll(`${orphanTag}[name]`)].forEach((orphanEl) => {
      const name = orphanEl.getAttribute("name");
      const hasCounterpart = [
        ...element.querySelectorAll(`${counterpartTag}[name]`),
      ].some((el) => el.getAttribute("name") === name);
      if (hasCounterpart) return;
      let sibling = orphanEl[siblingKey];
      while (sibling) {
        if (sibling.nodeName.toLowerCase() === "math") {
          const newEl = document.createElement(counterpartTag);
          newEl.setAttribute("name", name);
          sibling[insertMethod](newEl);
          return;
        }
        if (sibling.nodeType === Node.ELEMENT_NODE) return;
        sibling = sibling[siblingKey];
      }
    });
  };

const repairOrphanSuggestionsAroundMath = (content) => {
  const element = createElement({ content });
  const repair = repairOrphanSuggestionCounterpart(element);
  repair({
    orphanTag: "suggestion-end",
    counterpartTag: "suggestion-start",
    siblingKey: "previousSibling",
    insertMethod: "after",
  });
  repair({
    orphanTag: "suggestion-start",
    counterpartTag: "suggestion-end",
    siblingKey: "nextSibling",
    insertMethod: "before",
  });
  return element.innerHTML;
};

const removeOrphanTagsForTrackChanges = (content) => {
  const element = createElement({ content });

  const startElements = [
    ...element.querySelectorAll(
      "suggestion-start, [data-suggestion-start-before],[data-suggestion-start-after]",
    ),
  ];

  startElements.forEach((startElement) => {
    let correspondingEndEl = [];

    switch (true) {
      case startElement.hasAttribute("data-suggestion-start-before"):
        correspondingEndEl = startElement
          .getAttribute("data-suggestion-start-before")
          .split(",");
        break;
      case startElement.hasAttribute("data-suggestion-start-after"):
        correspondingEndEl = startElement
          .getAttribute("data-suggestion-start-after")
          .split(",");
        break;
      default:
        correspondingEndEl = startElement.getAttribute("name").split(",");
        break;
    }

    const uniqueEndElements = [...new Set(correspondingEndEl)];

    uniqueEndElements.forEach((endEl) => {
      const endElement = element.querySelector(
        `suggestion-end[name*="${endEl}"], [data-suggestion-end-before*="${endEl}"], [data-suggestion-end-after*="${endEl}"]`,
      );
      if (!endElement) {
        let newAttributes = [];
        switch (true) {
          case startElement.hasAttribute("data-suggestion-start-before"):
            newAttributes = startElement
              .getAttribute("data-suggestion-start-before")
              .split(",")
              .filter((item) => item !== endEl);
            if (newAttributes.length) {
              startElement.setAttribute(
                "data-suggestion-start-before",
                newAttributes.join(","),
              );
            } else {
              startElement.removeAttribute("data-suggestion-start-before");
            }
            break;
          case startElement.hasAttribute("data-suggestion-start-after"):
            newAttributes = startElement
              .getAttribute("data-suggestion-start-after")
              .split(",")
              .filter((item) => item !== endEl);
            if (newAttributes.length) {
              startElement.setAttribute(
                "data-suggestion-start-after",
                newAttributes.join(","),
              );
            } else {
              startElement.removeAttribute("data-suggestion-start-after");
            }
            break;
          default:
            newAttributes = startElement
              .getAttribute("name")
              .split(",")
              .filter((item) => item !== endEl);
            if (newAttributes.length) {
              startElement.setAttribute("name", newAttributes.join(","));
            } else {
              startElement.remove();
            }
            break;
        }
      }
    });
  });

  const endElements = [
    ...element.querySelectorAll(
      "suggestion-end, [data-suggestion-end-before],[data-suggestion-end-after]",
    ),
  ];

  endElements.forEach((endElement) => {
    let correspondingstartEl = [];

    switch (true) {
      case endElement.hasAttribute("data-suggestion-end-before"):
        correspondingstartEl = endElement
          .getAttribute("data-suggestion-end-before")
          .split(",");
        break;
      case endElement.hasAttribute("data-suggestion-end-after"):
        correspondingstartEl = endElement
          .getAttribute("data-suggestion-end-after")
          .split(",");
        break;
      default:
        correspondingstartEl = endElement.getAttribute("name").split(",");
        break;
    }

    const uniqueStartElements = [...new Set(correspondingstartEl)];

    uniqueStartElements.forEach((startEl) => {
      const startElement = element.querySelector(
        `suggestion-start[name*="${startEl}"], [data-suggestion-start-before*="${startEl}"], [data-suggestion-start-after*="${startEl}"]`,
      );
      if (!startElement) {
        let newAttributes = [];
        switch (true) {
          case endElement.hasAttribute("data-suggestion-end-before"):
            newAttributes = endElement
              .getAttribute("data-suggestion-end-before")
              .split(",")
              .filter((item) => item !== startEl);
            if (newAttributes.length) {
              endElement.setAttribute(
                "data-suggestion-end-before",
                newAttributes.join(","),
              );
            } else {
              endElement.removeAttribute("data-suggestion-end-before");
            }
            break;
          case endElement.hasAttribute("data-suggestion-end-after"):
            newAttributes = endElement
              .getAttribute("data-suggestion-end-after")
              .split(",")
              .filter((item) => item !== startEl);
            if (newAttributes.length) {
              endElement.setAttribute(
                "data-suggestion-end-after",
                newAttributes.join(","),
              );
            } else {
              endElement.removeAttribute("data-suggestion-end-after");
            }
            break;
          default:
            newAttributes = endElement
              .getAttribute("name")
              .split(",")
              .filter((item) => item !== startEl);
            if (newAttributes.length) {
              endElement.setAttribute("name", newAttributes.join(","));
            } else {
              endElement.remove();
            }
            break;
        }
      }
    });
  });
  return element.innerHTML;
};

const removeLabelfromTTS = (content) => {
  if (Array.isArray(content)) {
    return content.map((item) => {
      const { label, ...rest } = item;
      return rest;
    });
  }
  return content;
};

const cleanModifiedTTSSpans = (name) => (data) => (content) => {
  const tags = data?.[name.split(".")[0]]?.accessibilityElements || [];
  if (!tags.length) return content;

  const root = createElement({ content });
  let changed = false;

  for (const { identifier, accessibilityFeatures } of tags) {
    const tagText = accessibilityFeatures?.[0]?.text;
    if (!identifier || !tagText) continue;
    const spans = root.querySelectorAll(`span[id="${identifier}"]`);
    spans.forEach((span, i) => {
      if (i > 0) {
        span.replaceWith(...span.childNodes);
        changed = true;
        return;
      }
      const text = span.textContent;
      if (text.trim() === tagText.trim()) return;

      const tagTextStart = text.indexOf(tagText);
      if (tagTextStart === -1) return;

      const tagTextEnd = tagTextStart + tagText.length;
      const { firstChild, lastChild } = span;

      if (tagTextStart > 0 && firstChild?.nodeType === Node.TEXT_NODE) {
        span.before(
          document.createTextNode(firstChild.nodeValue.slice(0, tagTextStart)),
        );
        firstChild.nodeValue = firstChild.nodeValue.slice(tagTextStart);
        changed = true;
      }

      if (tagTextEnd < text.length && lastChild?.nodeType === Node.TEXT_NODE) {
        const keep = lastChild.nodeValue.length - (text.length - tagTextEnd);
        span.after(document.createTextNode(lastChild.nodeValue.slice(keep)));
        lastChild.nodeValue = lastChild.nodeValue.slice(0, keep);
        changed = true;
      }
    });
  }

  return changed ? root.innerHTML : content;
};

const emptyContentRE = /^(?:&nbsp;|\u00A0|<br\s*\/?>|\s)*$/;

const isEmptyParagraph = (p) => {
  return p.tagName === "P" && emptyContentRE.test(p.innerHTML.trim());
};

const removeEmptyNBSPParagraphs = (content) => {
  const element = document.createElement("div");
  element.innerHTML = content;
  let lastChild = element.lastChild;

  while (lastChild) {
    if (isEmptyParagraph(lastChild)) {
      lastChild.remove();
    } else {
      break;
    }
    lastChild = element.lastChild;
  }

  return element.innerHTML;
};

const transformContentFromEditor = (name) => (data) =>
  _.pipe([
    (content) => {
      // console.debug(
      //   `Content from editor (before transformation) for ${name}`,
      //   content,
      // );
      return content;
    },
    removeStyleFromMatchingElements("p[style]")("list-style-type"),
    removeNBSPAdjacentToIPR(name)(data),
    removeNBSPAdjacentToSpanMath,
    addStyleToTableHeader,
    addXMLNSAttributesToMathML,
    adjustItalicizedText,
    adjustMathMLSpacing,
    repairOrphanSuggestionsAroundMath,
    removeOrphanTagsForTrackChanges,
    removeEmptyNBSPParagraphs,
    removeSpaceFromStyles,
    removeSpecificStylesFromContent,
    removeLabelfromTTS,
    cleanModifiedTTSSpans(name)(data),
    // DOM navigation/manipulation above this line
    encodeMathMLTags,
    convertUTF8ToHTMLEntities,
    (content) => {
      // console.debug(
      //   `Content from editor (after transformation) for ${name}`,
      //   content,
      // );
      return content;
    },
  ]);

const removeSpaceFromStyles = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("*[style]")].forEach((el) => {
    const style = el
      .getAttribute("style")
      .replaceAll("; ", ";")
      .replaceAll(": ", ":");
    el.setAttribute("style", style);
  });

  return element.innerHTML;
};

const removeSpecificStylesFromContent = (content) => {
  const propertiesToRemove = [
    "color",
    "accent-color",
    "background-color",
    "border-block-color",
    "border-block-end-color",
    "border-block-start-color",
    "border-bottom-color",
    "border-color",
    "border-inline-color",
    "border-inline-end-color",
    "border-inline-start-color",
    "border-left-color",
    "border-right-color",
    "border-top-color",
    "caret-color",
    "column-rule-color",
    "outline-color",
    "scrollbar-color",
    "text-decoration-color",
    "text-emphasis-color",
  ];

  const element = document.createElement("div");
  element.innerHTML = content;

  element.querySelectorAll("*").forEach((el) => {
    if (el.tagName.toLowerCase() === "td") {
      const style = el.getAttribute("style");
      if (style) {
        let newStyle = style
          .split(";")
          .map((rule) => {
            const [property, value] = rule.split(":").map((str) => str.trim());
            return propertiesToRemove.includes(property)
              ? property === "background-color"
                ? `${property}: ${value}`
                : ""
              : rule;
          })
          .filter(Boolean)
          .join(";");

        if (!newStyle.trim()) {
          el.removeAttribute("style");
        } else {
          el.setAttribute("style", newStyle);
        }
      }
    } else {
      const style = el.getAttribute("style");
      if (style) {
        let newStyle = style
          .split(";")
          .map((rule) => {
            const [property] = rule.split(":").map((str) => str.trim());
            return propertiesToRemove.includes(property) ? "" : rule;
          })
          .filter(Boolean)
          .join(";");

        if (!newStyle.trim()) {
          el.removeAttribute("style");
        } else {
          el.setAttribute("style", newStyle);
        }
      }
    }
  });

  return element.innerHTML;
};

const transformMathMlMfenced = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("mfenced")].forEach((el) => {
    const closeAttr = el.getAttribute("close") || ")";
    const openAttr = el.getAttribute("open") || "(";

    const mrow = window.document.createElement("mrow");
    mrow.innerHTML = `<mo>${openAttr}</mo> ${el.innerHTML} <mo>${closeAttr}</mo>`;

    el.replaceWith(mrow);
  });

  return element.innerHTML;
};

const unwrapElements = (selector) => (element) => {
  Array.from(element.querySelectorAll(selector)).map((el) => {
    const childNodes = Array.from(el.childNodes);
    el.replaceWith(...childNodes);
  });
  return element;
};
const removePTagFromFoils = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("p")].forEach((p) => {
    if (p.textContent.trim() === "" && !p.children.length) {
      p.remove();
    }
  });

  return element.innerHTML;
};

const removeDivForInteractions = (content) => {
  const element = self.createElement({ content });
  [...element.querySelectorAll("div")].forEach((div) => {
    const firstChild = div.firstElementChild;
    if (firstChild && firstChild.hasAttribute("data-interaction-type")) {
      const childNodes = Array.from(div.childNodes);
      div.replaceWith(...childNodes);
    }
  });
  return element.innerHTML;
};

const wrapPlainTextWithP = (content) => {
  const element = self.createElement(content);
  element.innerHTML = content;

  Array.from(element.childNodes).forEach((child) => {
    // Function to replace top-level <div> elements with <p> elements
    if (
      child.nodeType === Node.ELEMENT_NODE &&
      child.tagName === "DIV" &&
      !child.classList.length &&
      !child.id
    ) {
      const p = document.createElement("p");
      while (child.firstChild) {
        p.appendChild(child.firstChild);
      }
      element.replaceChild(p, child);
    } else if (
      child.nodeType === Node.TEXT_NODE &&
      child.nodeValue.trim() !== ""
    ) {
      const p = document.createElement("p");
      p.textContent = child.nodeValue;
      element.replaceChild(p, child);
    }
  });

  // Function to unwrap a parent <p> tag that contains a <ul> or <ol>, while
  // preserving any sibling content (e.g. text before/after the list) so it is
  // not discarded when the surrounding <div> wrapper is removed. Empty
  // placeholders adjacent to the list (empty <p>, <p>&nbsp;</p>, <p><br></p>,
  // bare <br> and whitespace-only text) are dropped, and bare text is wrapped
  // in a <p> so downstream processing (e.g. TTS accessibility) can reach it.
  const unwrapListSibling = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      return isEmptyParagraph(node) || node.tagName === "BR" ? [] : [node];
    }
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.nodeValue.trim() === "") return [];
      const paragraph = document.createElement("p");
      paragraph.textContent = node.nodeValue;
      return [paragraph];
    }
    return [node];
  };
  Array.from(element.querySelectorAll("ul, ol")).forEach((child) => {
    const parent = child.parentNode;
    if (parent && parent.tagName === "P") {
      parent.replaceWith(
        ...Array.from(parent.childNodes).flatMap(unwrapListSibling),
      );
    }
  });

  return element.innerHTML;
};

const removeNewlineAndTab = (content) => {
  // Remove all newlines and tabs throughout the content
  return content ? content.replace(/[\n\t]+/g, "") : "";
};

const removeCdeIds = (content) => {
  const element = self.createElement({ content });

  [...element.querySelectorAll("*")].map((el) => {
    const classAttr = el.getAttribute("class");
    const idAttr = el.getAttribute("id");
    if (/cde_[^"]*/.test(idAttr) && !/srTransform/.test(classAttr)) {
      el.removeAttribute("id");
    }
  });

  return element.innerHTML;
};

export {
  removeNewlineAndTab,
  addStyleToTableHeader,
  addXMLNSAttributesToMathML,
  adjustMathMLSpacing,
  adjustItalicizedText,
  adjustEMText,
  adjustTableFormattingForAPI,
  adjustTableFormattingForUI,
  applyTTSAccessibility,
  convertSuggestionsForDisplay,
  convertTasksToUI,
  convertUTF8ToHTMLEntities,
  createDynamicContentElements,
  createElement,
  createHighlightElements,
  createLabelElements,
  createParagraphUUIDs,
  createSRAccessibilityTag,
  createTTSAccessibilityTag,
  deleteDynamicTags,
  deleteHighlights,
  deleteScreenReaderTags,
  deleteScreenReaderTagsFromContent,
  deleteTextToSpeechTags,
  deleteTextToSpeechTagsFromContent,
  encodeMathMLTags,
  expandRangeEnd,
  expandRangeStart,
  expandRangeToFindCommonNode,
  getAssociatedDynamicTags,
  getCharacterCount,
  getCleanContent,
  getCorrectDynamicTagPosition,
  getCorrectFoilContentFromItem,
  getCurrentDynamicTagPosition,
  getDocumentFragmentContents,
  getDynamicContentElement,
  getDynamicModifyContentTagRange,
  getDynamicModifyContentTagReplacementText,
  getDynamicTagOptions,
  getElements,
  getHasUnresolvedChangeTags,
  getItemNamesForTags,
  getOrderedElements,
  getScreenReaderTags,
  getSelectedText,
  getSelectedTextFromRange,
  getSentenceRanges,
  getSROptions,
  getSurroundingElements,
  getTextContent,
  getTextToSpeechTags,
  getTrimmedRange,
  getTTSOptions,
  editScreenReaderTagContent,
  insertDynamicTagIntoContent,
  insertModifyContentTag,
  isBreakParagraphMarkElement,
  isElement,
  isHighlightMarkElement,
  isLabelMarkElement,
  isLetterPointLabel,
  isModifyContentMarkElement,
  isParagraphNumberLabel,
  isSentenceNumberLabel,
  isSugggestionElement,
  isTextNodeInElement,
  isVisible,
  orderDynamicTags,
  rangeInHighlight,
  rangeInNode,
  rangeInPattern,
  removeAllMarkTags,
  removeClassMsoNormalAttr,
  removeFontFamilyFromStyle,
  removeFromContent,
  removeLabelfromTTS,
  removeInsertCorrectTextPlaceholders,
  removeMarkDataSuggestion,
  removeNBSPAdjacentToIPR,
  removeNBSPAdjacentToSpanMath,
  removeSpaceFromStyles,
  removeEmptyNBSPParagraphs,
  removePTagFromFoils,
  removeDivForInteractions,
  removeFontWeight400FromStyle,
  removeCdeIds,
  removeUnnecessaryMarkTags,
  removeXMLNSAttributesFromMathML,
  repairOrphanSuggestionsAroundMath,
  replaceNBSPAdjacentToIPR,
  transformContentForEditor,
  transformContentFromEditor,
  transformMathMlMfenced,
  unwrapElements,
  unwrapPlainTableCellParagraphs,
  updateFoilTTSPrefixes,
  wrapPlainTextWithP,
};
